<template>
  <div id="pdf-area" class="full-width">
    <div
      :class="printingMode && !hideLegacyPrintingHeader ? '' : 'property-compare'"
      class="q-gutter-y-lg q-mx-auto"
    >
      <warning-banner v-if="report && comparableAddresses.length > maxCompLimit && !printingMode">
        The report contains {{ comparableAddresses.length }} properties. For faster loading, the
        first
        {{ maxCompLimit }}
        comparables are being displayed.
        <template #action>
          <q-btn
            v-if="!showReadOnlyView"
            :color="$q.dark.isActive ? 'deep-orange-1' : 'deep-orange-10'"
            :to="{ name: 'Edit Property Report', params: { reportId: report.id } }"
            no-caps
            outline
          >
            Edit Report
          </q-btn>
        </template>
      </warning-banner>
      <div v-if="printingMode && !hideLegacyPrintingHeader" class="text-center q-mb-md">
        <img :src="exportLogo" alt="Company logo" width="300px" />
        <div class="text-h6">
          {{ pageName }}
        </div>
        <div v-if="dateRange">
          {{ format(dateRange.from, 'MMMM d, yyyy') }} -
          {{ format(dateRange.to, 'MMMM d, yyyy') }}
        </div>
      </div>
      <div id="scroll-anchor" data-html2canvas-ignore style="position: absolute; top: -150px" />
      <keep-alive>
        <template v-if="printingMode && isActiveSection('overview', Section.PROPERTY_GRID)">
          <!-- a different grid for each group of 10 comps -->
          <property-compare-overview
            v-for="(segment, idx) in segmentedFilteredProperties"
            id="intercom-property-compare-overview"
            :key="idx"
            :date-range="dateRange"
            :details="comparableDetails"
            :expanded-unit-history="expandedUnitHistory"
            :filtered-properties="segment"
            :legacy-printing-mode="!hideLegacyPrintingHeader"
            :printing-mode="printingMode"
            :similarities="similarities"
            :start-comp-numbering-at="numCompsPerSegment * idx"
            :subject-detail="subjectDetail"
            class="break-after"
          />
        </template>
        <template v-else-if="isActiveSection('overview', Section.PROPERTY_GRID)">
          <property-compare-overview
            id="intercom-property-compare-overview"
            :date-range="dateRange"
            :details="comparableDetails"
            :expanded-unit-history="expandedUnitHistory"
            :filtered-properties="filteredProperties"
            :printing-mode="printingMode"
            :similarities="similarities"
            :subject-detail="subjectDetail"
          />
        </template>
      </keep-alive>
      <property-compare-market-alerts
        v-if="!isNewDevelopment && isActiveSection('overview', Section.MARKET_ALERTS)"
        id="intercom-property-compare-market-alerts"
        :details="comparableDetails"
        :printing-mode="printingMode"
        :subject-detail="subjectDetail"
        class="break-after"
      />
      <property-compare-poi-google-map
        v-if="isActiveSection('overview', Section.POI_MAP)"
        id="intercom-property-compare-poi-google-map"
        ref="poiGoogleMap"
        :comparables="comparableDetails"
        :printing-mode="printingMode"
        :subject-detail="subjectDetail"
        class="break-after"
      />
      <div class="break-after">
        <property-compare-google-map
          v-if="isActiveSection('property', Section.COMP_MAP)"
          id="intercom-property-compare-google-map"
          ref="compGoogleMap"
          :filtered-properties="filteredProperties"
          :legacy-printing-mode="!hideLegacyPrintingHeader"
          :loading="loading"
          :printing-mode="printingMode"
          :similarities="similarities"
          :subject-detail="subjectDetail"
          @map-snapshot="onMapSnapshot"
          @map-snapshot-alternative="onMapSnapshotAlternative"
        />
      </div>
      <q-linear-progress
        v-if="progressPercentage !== null"
        :value="progressPercentage"
        class="q-my-auto"
        color="primary"
      />
      <q-card
        v-if="loading"
        :class="{ 'bg-grey-9  ': $q.dark.isActive }"
        bordered
        class="text-center bg-grey-3 q-pa-md"
        flat
      >
        Tip: {{ randomTip() }}
      </q-card>
      <property-compare-building-info
        v-if="isActiveSection('property', Section.BUILDING_INFORMATION)"
        id="intercom-property-compare-building-info"
        :date-range="dateRange"
        :filtered-properties="filteredProperties"
        :loading="loading"
        :printing-mode="printingMode"
        :report-id="report?.id || null"
        :similarities="similarities"
        :skeleton-rows="expectedProperties"
        :subject-detail="subjectDetail"
        :toggleable-columns="getToggleableColumnsForSection(Section.BUILDING_INFORMATION)"
        :visible-columns="getVisibleColumnsForSection(visibleColumns, Section.BUILDING_INFORMATION)"
        class="break-after"
      />
      <lazy-wrapper
        v-if="!loading && isActiveSection('property', Section.UNIT_TYPE_METRICS)"
        id="intercom-property-compare-unit-type-availability"
        :force-visible="printingMode"
      >
        <property-compare-unit-type-metrics
          :date-range="dateRange"
          :filtered-properties="filteredProperties"
          :printing-mode="printingMode"
          class="break-after"
        />
      </lazy-wrapper>
      <lazy-wrapper
        v-if="!loading && isActiveSection('property', Section.UNIT_TYPE_TABLE)"
        id="intercom-property-compare-unit-type-table"
        :force-visible="printingMode"
      >
        <property-compare-unit-type-table
          :date-range="dateRange"
          :expanded-unit-history="expandedUnitHistory"
          :filtered-properties="filteredProperties"
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          :toggleable-columns="getToggleableColumnsForSection(Section.UNIT_TYPE_TABLE)"
          :visible-columns="getVisibleColumnsForSection(visibleColumns, Section.UNIT_TYPE_TABLE)"
          class="break-after"
        />
      </lazy-wrapper>
      <lazy-wrapper
        v-if="
          !loading &&
          isActiveSection('property', Section.PROPERTY_RANKINGS_CHART) &&
          filteredProperties.length > 1
        "
        :force-visible="printingMode"
      >
        <property-compare-rankings-chart
          :filtered-properties="filteredProperties"
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          class="break-after"
        />
      </lazy-wrapper>
      <lazy-wrapper
        v-if="!loading && isActiveSection('property', Section.ESTIMATED_UNIT_MIX)"
        id="intercom-property-estimated-unit-mix"
        :force-visible="printingMode"
      >
        <property-compare-estimated-rent-roll
          :details="comparableDetails"
          :expanded-unit-history="expandedUnitHistory"
          :printing-mode="printingMode"
          :selected-buildings="selectedBuildings"
          :subject-detail="subjectDetail"
          :toggleable-columns="getToggleableColumnsForSection(Section.ESTIMATED_UNIT_MIX)"
          :visible-columns="getVisibleColumnsForSection(visibleColumns, Section.ESTIMATED_UNIT_MIX)"
          class="break-after"
        />
      </lazy-wrapper>
      <lazy-wrapper
        v-if="!loading && isActiveSection('property', Section.VOLUME_OVER_TIME)"
        id="intercom-property-compare-volume-over-time"
        :force-visible="printingMode"
      >
        <property-compare-volume-over-time
          :date-range="dateRange"
          :excluded-amenities="excludedAmenities.map((_) => _.value)"
          :expanded-unit-history="expandedUnitHistory"
          :filtered-properties="filteredProperties"
          :included-amenities="includedAmenities.map((_) => _.value)"
          :lease-terms="leaseTermSelectionSet"
          :listings-selection-value="listingStatusFilter"
          :printing-mode="printingMode"
          :sqft-range="sqftRange"
          :subject-detail="subjectDetail"
          :unit-types="selectedUnitTypeValues"
          class="break-after"
        />
      </lazy-wrapper>
      <lazy-wrapper
        v-if="!loading && isActiveSection('property', Section.CHART_COMPARE)"
        id="intercom-property-compare-chart-compare"
        :force-visible="printingMode"
      >
        <property-compare-bubble-chart
          :date-range="dateRange"
          :expanded-unit-history="expandedUnitHistory"
          :filtered-properties="filteredProperties"
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          class="break-after"
        />
      </lazy-wrapper>
      <lazy-wrapper
        v-if="!loading && isActiveSection('property', Section.RENT_TREND)"
        id="intercom-property-compare-chart-history"
        :force-visible="printingMode"
      >
        <property-compare-rent-trends
          :date-range="dateRange"
          :expanded-unit-history="expandedUnitHistory"
          :filtered-properties="filteredProperties"
          :listings-selection-value="listingStatusFilter"
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          class="break-after"
        />
      </lazy-wrapper>
      <lazy-wrapper
        v-if="!loading && isActiveSection('property', Section.EXPOSURE_CHART)"
        id="intercom-property-compare-chart-exposure"
        :force-visible="printingMode"
      >
        <property-compare-occupancy-chart
          :date-range="dateRange"
          :details="comparableDetails"
          :printing-mode="printingMode"
          :selected-buildings="selectedBuildings"
          :subject-detail="subjectDetail"
          class="break-after"
        />
      </lazy-wrapper>
      <lazy-wrapper
        v-if="!loading && isActiveSection('property', Section.FUTURE_AVAILABILITY)"
        id="intercom-property-compare-future-availability"
        :force-visible="printingMode"
      >
        <property-compare-future-availability
          :date-range="dateRange"
          :expanded-unit-history="expandedUnitHistory"
          :filtered-properties="filteredProperties"
          :printing-mode="printingMode"
          class="break-after"
        />
      </lazy-wrapper>
      <lazy-wrapper
        v-if="
          hasRentProjections &&
          !loading &&
          isActiveSection('property', Section.FIVE_YEAR_RENT_FORECAST)
        "
        id="intercom-property-five-year-rent-forecast"
        :force-visible="printingMode"
      >
        <property-compare-rent-projections
          :subject-property="subjectDetail"
          class="break-after"
          :printing-mode="printingMode"
        />
      </lazy-wrapper>
      <lazy-wrapper
        v-if="
          hasRentProjections &&
          !loading &&
          isActiveSection('property', Section.FIVE_YEAR_OCCUPANCY_FORECAST)
        "
        id="intercom-property-five-year-occupancy-forecast"
        :force-visible="printingMode"
      >
        <property-compare-occupancy-projections
          class="break-after"
          :printing-mode="printingMode"
          :subject-property="subjectDetail"
        />
      </lazy-wrapper>
      <div
        v-if="isActiveSection('unit', Section.UNIT_METRICS)"
        id="intercom-property-compare-unit-metrics"
      >
        <property-compare-unit-metrics
          :date-range="dateRange"
          :filtered-properties="filteredProperties"
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          class="break-after"
        />
      </div>
      <div
        v-if="isActiveSection('unit', Section.UNIT_DETAILS)"
        id="intercom-property-compare-unit-availability"
      >
        <!-- hide this component on print -->
        <property-compare-unit-details
          v-if="!printingMode"
          :date-range="dateRange"
          :expanded-unit-history="expandedUnitHistory"
          :printing-mode="printingMode"
          :units-with-properties="filteredPropertiesWithUnitProps"
          class="break-after"
        />
      </div>
      <div v-if="isActiveSection('unit', Section.LEASE_TERMS)">
        <property-compare-lease-terms
          id="intercom-property-compare-lease-terms"
          :date-range="dateRange"
          :expanded-unit-history="expandedUnitHistory"
          :filtered-properties="filteredProperties"
          :listings-selection-value="listingStatusFilter"
          :printing-mode="printingMode"
          class="break-after"
        />
      </div>
      <div
        v-if="isActiveSection('unit', Section.PRICE_RECOMMENDATION)"
        id="intercom-property-compare-recent-closings"
      >
        <property-compare-price-recommendation
          :comps-ids="comparableDetails.map((_) => _.id)"
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          :unit-types="selectedUnitTypeValues"
          class="break-after"
        />
      </div>
      <div
        v-if="isActiveSection('unit', Section.UNIT_PRICES_OVER_TIME)"
        id="intercom-property-compare-unit-prices-over-time"
      >
        <property-compare-unit-prices-over-time
          :date-range="dateRange"
          :expanded-unit-history="expandedUnitHistory"
          :filtered-properties="filteredProperties"
          :printing-mode="printingMode"
          class="break-after"
        />
      </div>
      <div
        v-if="isActiveSection('feesAndAmenities', Section.FEES)"
        id="intercom-property-compare-fees"
      >
        <property-compare-fees
          :filtered-properties="filteredProperties"
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          class="break-after"
        />
      </div>
      <div
        v-if="isActiveSection('feesAndAmenities', Section.SPECIALS_CONCESSIONS)"
        id="intercom-property-compare-concessions"
      >
        <property-compare-concessions
          :date-range="dateRange"
          :filtered-properties="filteredProperties"
          :printing-mode="printingMode"
          class="break-after"
        />
      </div>
      <div
        v-if="isActiveSection('feesAndAmenities', Section.FINISHES)"
        id="intercom-property-compare-images"
      >
        <property-compare-finishes
          :filtered-properties="filteredProperties"
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          class="break-after"
        />
      </div>
      <div
        v-if="isActiveSection('feesAndAmenities', Section.AMENITIES)"
        id="intercom-property-compare-images"
      >
        <property-compare-amenities
          :filtered-properties="filteredProperties"
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          :passed-in-amenity-selections="printingMode ? componentOptions.amenitySelections : null"
          class="break-after"
          @update:amenity-selections="componentOptions.amenitySelections = $event"
        />
      </div>
      <div
        v-if="
          (hasFinancialAnalysis || showExpenseBenchmarkUpsell || isSampleView) &&
          isActiveSection('expenseBenchmarks', Section.FINANCIAL_ANALYSIS)
        "
        id="intercom-property-compare-financial-analysis"
      >
        <property-compare-financial-analysis
          :printing-mode="printingMode"
          :show-upsell="showExpenseBenchmarkUpsell"
          :subject-detail="subjectDetail"
          class="break-after"
        />
      </div>
      <div
        v-if="
          (hasFinancialAnalysis || isSampleView) &&
          isActiveSection('expenseBenchmarks', Section.TAX_HISTORY)
        "
        id="intercom-property-compare-tax-history"
      >
        <property-compare-tax-history
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          class="break-after"
        />
      </div>
      <div
        v-if="isActiveSection('revenueManagement', Section.RECOMMENDATION_HIGHLIGHTS)"
        id="intercom-property-compare-recommendation-highlights"
      >
        <property-compare-recommendation-highlights
          :date-range="dateRange"
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          class="break-after"
        />
      </div>
      <div
        v-if="
          hasRevManagementFeature &&
          isActiveSection('revenueManagement', Section.UNIT_MIX_RECOMMENDATION)
        "
        id="intercom-property-compare-unit-mix-recommendation"
      >
        <property-compare-unit-mix-recommendation
          :date-range="dateRange"
          :details="comparableDetails"
          :expanded-unit-history="expandedUnitHistory"
          :printing-mode="printingMode"
          :subject-detail="subjectDetail"
          :unit-types="selectedUnitTypeValues"
          class="break-after"
        />
      </div>
      <lazy-wrapper
        v-if="
          hasRentProjections && !loading && isActiveSection('property', Section.LEASING_VELOCITY)
        "
        id="intercom-property-leasing-velocity"
        :force-visible="printingMode"
      >
        <property-compare-leasing-velocity
          class="break-after"
          :printing-mode="printingMode"
          :subject-property="subjectDetail"
        />
      </lazy-wrapper>
      <property-compare-debug
        v-if="activeTab === 'debug'"
        :filtered-properties="filteredProperties"
        :subject-detail="subjectDetail"
      />
      <div v-else-if="!printingMode" class="q-mt-xl text-center text-grey-8">
        Help us make HelloData even better! Share your feedback, feature requests, and data
        visualization ideas by leaving us a message
        <span class="text-primary cursor-pointer" @click="openIntercom">in our chat box.</span>
      </div>
    </div>
  </div>
  <safe-teleport to="#toolbar-portal">
    <q-toolbar
      id="intercom-toolbar-1"
      :class="{ 'q-px-md': $q.screen.gt.xs, 'q-px-lg': $q.screen.gt.md }"
      :style="`border-bottom: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'}`"
      class="q-px-sm row justify-center"
    >
      <div class="property-compare full-width row">
        <!-- Street View -->
        <q-img
          v-if="getStreetViewUrlUnion && $q.screen.gt.sm && !streetviewError"
          :src="getStreetViewUrlUnion"
          class="q-mr-md q-my-auto"
          data-private
          no-spinner
          style="width: 90px; height: 100%"
          @error="streetviewError = true"
        />

        <!-- Street View Error -->
        <div
          v-if="$q.screen.gt.sm && streetviewError"
          :class="{ 'bg-grey-9': $q.dark.isActive }"
          class="column justify-center bg-grey-3 q-mr-md q-my-auto"
          style="width: 90px; height: 59px"
        >
          <div class="row justify-center">
            <q-icon class="text-grey-5" name="apartment" size="lg" />
          </div>
        </div>

        <!-- Property Report Title -->
        <div>
          <div style="font-size: 20px; font-weight: 500">
            <div class="row q-gutter-x-sm q-pt-sm">
              <div>
                <span v-if="report === null" class="text-accent">Report Preview:</span>
                {{ pageName }}
              </div>
              <div id="intercom-last-updated" class="row items-center">
                <q-skeleton v-if="!lastUpdated" class="q-px-sm" style="width: 100px" type="text" />
                <q-btn
                  v-else
                  class="q-px-sm q-pt-xs"
                  clickable
                  color="primary"
                  dense
                  flat
                  no-caps
                  size="sm"
                  @click="$tracker.openedPopup('last_updated')"
                >
                  Updated {{ lastUpdated }} ago
                  <q-icon name="arrow_drop_down" size="small" />
                  <q-menu transition-duration="100">
                    <q-separator />
                    <q-markup-table dense flat square>
                      <thead>
                        <tr class="text-left">
                          <th class="text-left" scope="col">Building</th>
                          <th class="text-left" scope="col">Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="x in allProperties" :key="x.id">
                          <td>
                            {{ x.building_name || x.street_address }}
                          </td>
                          <td>{{ formatDistance(new Date(), parseISO(x.created_on)) }} ago</td>
                        </tr>
                      </tbody>
                    </q-markup-table>
                  </q-menu>
                </q-btn>
                <q-btn
                  v-if="report?.metadata && Object.keys(report?.metadata).length > 0"
                  :color="$q.dark.isActive ? 'grey-4' : 'grey-9'"
                  class="q-pt-xs"
                  clickable
                  dense
                  flat
                  no-caps
                  size="sm"
                >
                  <span class="text-weight-regular"> Metadata </span>
                  <i class="material-icons hd-icon"> arrow_drop_down</i>
                  <q-menu
                    anchor="top right"
                    self="bottom middle"
                    style="width: 300px"
                    transition-duration="100"
                  >
                    <q-card flat>
                      <q-card-section>
                        <div class="text-weight-bold">Metadata</div>
                      </q-card-section>
                      <q-separator />
                      <q-card-section>
                        <div v-for="key in Object.keys(report.metadata)" :key="key">
                          <strong> {{ key }}: </strong>
                          <span class="q-pl-sm">
                            {{ report.metadata[key] }}
                          </span>
                        </div>
                      </q-card-section>
                    </q-card>
                  </q-menu>
                </q-btn>
              </div>
              <template v-if="report">
                <q-chip
                  v-for="tag in report.tags"
                  :key="tag"
                  :ripple="false"
                  class="q-my-auto"
                  color="accent"
                  dense
                  no-caps
                  outline
                >
                  {{ tag }}
                </q-chip>
              </template>
            </div>
          </div>
          <q-tabs
            id="intercom-tabs"
            v-model="activeTab"
            :active-color="report === null ? 'accent' : 'primary'"
            align="left"
            dense
            no-caps
            @update:model-value="(tab) => $tracker.changedTab(activeTab, tab, 'property_report')"
          >
            <q-tab
              v-for="tab in tabs"
              :id="`intercom-tabs-${tab.name}`"
              :key="tab.name"
              :disable="tab.disable"
              :name="tab.name"
              :ripple="false"
              class="tab q-px-xs q-mr-xs"
            >
              <div class="row items-center">
                {{ tab.label }}
                <q-tooltip
                  v-if="tab.name === 'expenseBenchmarks' && financialAnalysisTooltip"
                  max-width="250px"
                >
                  {{ financialAnalysisTooltip }}
                </q-tooltip>
                <q-badge
                  v-if="tab.name === 'revenueManagement' && $q.screen.gt.md"
                  class="q-px-xs q-ml-xs"
                  color="primary"
                  outline
                  style="font-size: 8px"
                >
                  BETA
                </q-badge>
              </div>
            </q-tab>
          </q-tabs>
        </div>
        <q-space v-if="$q.screen.gt.xs" />
        <div
          v-if="report?.id !== -1 && subjectDetail && hasMarketAnalytics"
          class="q-gutter-sm self-center q-mr-sm"
        >
          <q-btn-dropdown
            :disable="!subjectDetail"
            color="orange"
            label="View Market"
            no-caps
            outline
            unelevated
          >
            <q-list style="min-width: 180px">
              <q-item v-close-popup v-ripple clickable @click="viewMarket('auto')">
                <q-item-section>
                  <div class="flex items-center">
                    <q-icon left name="auto_awesome" size="xs" />
                    <div class="q-ml-xs">Auto Market</div>
                  </div>
                </q-item-section>
                <q-item-section side>
                  <q-icon name="info" size="xs" />
                  <q-tooltip>Derived from all similar properties</q-tooltip>
                </q-item-section>
              </q-item>
              <q-separator />
              <q-item v-close-popup v-ripple clickable @click="viewMarket(1)">
                <q-item-section>
                  <div class="flex items-center">
                    <q-icon left name="location_on" size="xs" />
                    <div class="q-ml-xs">1mi Radius</div>
                  </div>
                </q-item-section>
              </q-item>
              <q-item v-close-popup v-ripple clickable @click="viewMarket(3)">
                <q-item-section>
                  <div class="flex items-center">
                    <q-icon left name="location_on" size="xs" />
                    <div class="q-ml-xs">3mi Radius</div>
                  </div>
                </q-item-section>
              </q-item>
              <q-item v-close-popup v-ripple clickable @click="viewMarket(5)">
                <q-item-section>
                  <div class="flex items-center">
                    <q-icon left name="location_on" size="xs" />
                    <div class="q-ml-xs">5mi Radius</div>
                  </div>
                </q-item-section>
              </q-item>
            </q-list>
          </q-btn-dropdown>
        </div>
        <div id="intercom-toolbar-1-actions" class="row items-center q-gutter-sm self-center">
          <q-btn-dropdown
            v-if="report?.id !== -1"
            id="intercom-export"
            :loading="downloadExcelLoading || downloadPdfLoading"
            no-caps
            outline
          >
            <template #label>
              <q-icon
                v-if="$q.screen.gt.md || $q.screen.lt.md"
                class="q-mr-sm"
                name="download"
                size="medium"
              />
              <span v-if="$q.screen.gt.sm">Export</span>
            </template>
            <q-list style="min-width: 150px">
              <q-item
                v-close-popup
                v-ripple
                :disable="!subject || !comparableAddresses.length"
                clickable
                @click="downloadExcel(false)"
              >
                <q-item-section side>
                  <q-icon name="table_view" size="xs" />
                </q-item-section>
                <q-item-section> Excel </q-item-section>
                <q-tooltip v-if="!subject">
                  You must select a subject property to export.
                </q-tooltip>
                <q-tooltip v-else-if="!comparableAddresses.length">
                  Please select at least 1 comparable property to export.
                  <br />
                  Currently only the subject property is selected.
                </q-tooltip>
              </q-item>
              <q-item v-close-popup v-ripple clickable @click="showPdfDialog = true">
                <q-item-section side>
                  <q-icon name="picture_as_pdf" size="xs" />
                </q-item-section>
                <q-item-section> PDF </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                v-ripple
                :disable="
                  !subject || !comparableAddresses.length || comparableAddresses.length > 30
                "
                clickable
                @click="openReportDialog"
              >
                <q-tooltip v-if="comparableAddresses.length > 30">
                  You can only select up to 30 comparable properties.
                </q-tooltip>
                <q-tooltip v-else-if="!subject">
                  You must select a subject property to generate a report.
                </q-tooltip>
                <q-tooltip v-else-if="!comparableAddresses.length">
                  Please select at least 1 comparable property to generate a report.
                  <br />
                  Currently only the subject property is selected.
                </q-tooltip>
                <q-item-section side>
                  <q-icon name="photo_filter" size="xs" />
                </q-item-section>
                <q-item-section> AI Reports </q-item-section>
                <q-item-section side>
                  <q-badge class="q-ml-sm" color="primary" text-color="white"> Beta </q-badge>
                </q-item-section>
              </q-item>
              <q-item
                v-if="hasAPI"
                v-close-popup
                v-ripple
                :disable="!subject && !comparableAddresses.length"
                clickable
                @click="showApiDialog = true"
              >
                <q-item-section side>
                  <q-icon name="code" size="xs" />
                </q-item-section>
                <q-item-section> API </q-item-section>
              </q-item>
              <template v-if="isHelloDataEmployee">
                <q-separator />
                <q-item-label header> Internal </q-item-label>
                <q-item
                  v-close-popup
                  v-ripple
                  :disable="!subject || !comparableAddresses.length"
                  clickable
                  @click="downloadExcel(true)"
                >
                  <q-item-section> Excel (Dev) </q-item-section>
                </q-item>
                <q-item
                  v-close-popup
                  v-ripple
                  :disable="!subject || !comparableAddresses.length"
                  clickable
                  @click="getExcelJsonPayload()"
                >
                  <q-item-section> JSON Query </q-item-section>
                </q-item>
                <q-item
                  v-close-popup
                  v-ripple
                  :disable="!subject || !comparableAddresses.length"
                  clickable
                  @click="downloadJson()"
                >
                  <q-item-section> JSON </q-item-section>
                </q-item>
              </template>
            </q-list>
          </q-btn-dropdown>
          <button-share
            v-if="reportInOrg && !showReadOnlyView"
            :report="report"
            :show-icon="$q.screen.gt.md || $q.screen.lt.md"
          />
          <div v-else-if="!isSampleView" class="q-pa-none">
            <q-chip no-caps outline color="primary" class="self-center q-ma-none">
              View Only
              <q-icon name="o_info" size="sm" class="q-ml-sm">
                <q-tooltip max-width="250px">
                  <span v-if="!reportInOrg">
                    This report is not found in your organization so an external view is being
                    shown.
                  </span>
                  <span v-else-if="!canEditPropertyReports">
                    You do not have the edit property reports permission. Please contact
                    organization admin for access.
                  </span>
                  <span v-else-if="userHasReadOnlyAccess">
                    Report was shared with Viewer only access. Please contact report owner for
                    Editor access.
                  </span>
                </q-tooltip>
              </q-icon>
            </q-chip>
          </div>
          <slot
            :has-property-reports="hasPropertyReports"
            :make-copy="makeCopy"
            name="primay-button"
          >
            <q-btn
              v-if="report && reportInOrg && !showReadOnlyView"
              id="intercom-edit-report"
              :to="{ name: 'Edit Property Report', params: { reportId: report.id } }"
              color="primary"
              no-caps
              unelevated
            >
              <q-icon
                v-if="$q.screen.gt.md || $q.screen.lt.md"
                class="q-mr-sm"
                name="edit"
                size="medium"
              />
              <span v-if="$q.screen.gt.sm">
                Edit
                <template v-if="$q.screen.gt.md"> Report</template>
              </span>
            </q-btn>
          </slot>
        </div>
      </div>
    </q-toolbar>
    <q-toolbar
      id="intercom-toolbar-2"
      :class="{ 'q-px-md': $q.screen.gt.xs, 'q-px-lg': $q.screen.gt.md }"
      :style="`border-bottom: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'}`"
      class="row q-px-sm justify-center"
    >
      <div
        v-show="activeTab !== 'expenseBenchmarks' && activeTab !== 'revenueManagement'"
        class="property-compare full-width row justify-end"
      >
        <div id="intercom-toolbar-2-filters" class="row q-gutter-sm">
          <q-btn-group v-if="$q.screen.gt.sm" outline>
            <select-options
              id="intercom-toolbar-2-properties-filter"
              v-model="selectedBuildings"
              :columns="propertiesSelectionColumns"
              :options-function="getBuildingOptions"
              label="Properties"
              no-empty
              @update:model-value="handleBuildingChange"
            >
              <template #extra-column-checkbox-1>
                <q-checkbox
                  :model-value="perBedAllChecked"
                  dense
                  keep-color
                  :color="$q.dark.isActive ? 'grey-7' : 'grey-5'"
                  @click="perBedAllChecked ? clearPerBed() : fillPerBed()"
                />
              </template>
              <template #extra-column-1="{ option }">
                <q-checkbox
                  :model-value="pricePerBed[option.value] || false"
                  :val="option.label"
                  dense
                  @update:model-value="toggleStudent(option.value, $event)"
                />
              </template>
            </select-options>
            <q-separator vertical />
            <select-options
              id="intercom-toolbar-2-unit-type-filter"
              v-model="selectedUnitType"
              :options-function="getUnitTypeOptions"
              grouped-selections-key="bed"
              grouped-selections-label="br"
              label="Unit Types"
              no-empty
              @update:model-value="(ut) => $tracker.changedFilter('unit_types', 'toolbar', ut)"
            />
            <q-separator vertical />
            <min-max-filter
              id="intercom-toolbar-2-sqft-filter"
              :max="sqftRange.max"
              :min="sqftRange.min"
              title="Square Feet"
              unit="ft²"
              use-values-for-label
              @hide="changedSqftRange"
              @update-range="changedSqftRange"
            >
              Sqft
            </min-max-filter>
            <q-separator vertical />
            <select-options
              id="intercom-toolbar-2-lease-term-filter"
              v-model="leaseTermSelection"
              :options-function="getLeaseTermOptions"
              auto-submit
              label="Lease Terms"
              @update:model-value="
                (value) => $tracker.changedFilter('lease_term', 'toolbar', value)
              "
            />
            <q-separator vertical />
            <select-options
              id="intercom-toolbar-2-listings-filter"
              v-model="listingsSelection"
              :options="listingsOptions"
              label="Status"
              no-empty
              @update:model-value="
                (value) => $tracker.changedFilter('availability', 'toolbar', value)
              "
            />
            <q-separator vertical />
            <include-exclude-select-options
              id="intercom-toolbar-2-amenities-filter"
              :excluded-options="excludedAmenities"
              :included-options="includedAmenities"
              :options-function="getAmenitiesList"
              alphabetical-sort
              label="Amenities"
              title="Unit Amenities"
              tooltip="Extracted from unit-level text available on property websites and floorplan images."
              @apply="handleAmenityApply"
            />
            <q-separator vertical />
            <include-exclude-select-options
              :excluded-options="excludedAmiLevels"
              :included-options="includedAmiLevels"
              :options-function="getAmiLevelsList"
              label="AMI Levels"
              title="Estimated AMI Levels"
              use-filter-help-text-slots
              @apply="handleAmiLevelApply"
            >
              <template #filter-help-text>
                Filter units based on affordability thresholds tied to a percentage of the local
                Area Median Income (AMI).
              </template>
            </include-exclude-select-options>
            <q-separator vertical />
            <date-range-picker
              id="intercom-toolbar-2-date-filter"
              :date-range="dateRange"
              :default-date-range="defaultDateRange"
              :dense="!denseDateRangePicker"
              show-set-default
              title="Date Availability"
              @update="changedDateRange"
              @set-default="setDefaultDateRange"
            />
          </q-btn-group>
          <div v-else>
            <q-btn-dropdown no-caps outline>
              <template #label>
                <q-icon class="q-mr-sm" name="filter_list" size="xs" />
                Filters
              </template>
              <q-list>
                <q-item>
                  <q-item-section>
                    <select-options
                      id="intercom-toolbar-2-unit-type-filter"
                      v-model="selectedUnitType"
                      :options-function="getUnitTypeOptions"
                      grouped-selections-key="bed"
                      grouped-selections-label="br"
                      label="Unit Types"
                      no-empty
                      @update:model-value="
                        (ut) => $tracker.changedFilter('unit_types', 'toolbar', ut)
                      "
                    />
                  </q-item-section>
                </q-item>
                <q-item>
                  <q-item-section>
                    <min-max-filter
                      id="intercom-toolbar-2-sqft-filter"
                      :max="sqftRange.max"
                      :min="sqftRange.min"
                      title="Square Feet"
                      unit="ft²"
                      use-values-for-label
                      @hide="changedSqftRange"
                      @update-range="changedSqftRange"
                    >
                      Sqft
                    </min-max-filter>
                  </q-item-section>
                </q-item>
                <q-item>
                  <q-item-section>
                    <select-options
                      id="intercom-toolbar-2-lease-term-filter"
                      v-model="leaseTermSelection"
                      :options-function="getLeaseTermOptions"
                      auto-submit
                      label="Lease Terms"
                      @update:model-value="
                        (value) => $tracker.changedFilter('lease_term', 'toolbar', value)
                      "
                    />
                  </q-item-section>
                </q-item>
                <q-item>
                  <q-item-section>
                    <select-options
                      id="intercom-toolbar-2-listings-filter"
                      v-model="listingsSelection"
                      :options="listingsOptions"
                      label="Status"
                      no-empty
                      @update:model-value="
                        (value) => $tracker.changedFilter('availability', 'toolbar', value)
                      "
                    />
                  </q-item-section>
                </q-item>
                <q-item>
                  <q-item-section>
                    <include-exclude-select-options
                      id="intercom-toolbar-2-amenities-filter"
                      :aliases="{ 'affordable.lihtc': 'Affordable LIHTC' }"
                      :excluded-options="excludedAmenities"
                      :included-options="includedAmenities"
                      :options-function="getAmenitiesList"
                      label="Amenities"
                      title="Unit Amenities"
                      tooltip="Extracted from unit-level text available on property websites and floorplan images."
                      @apply="handleAmenityApply"
                    />
                  </q-item-section>
                </q-item>
                <q-item>
                  <q-item-section>
                    <include-exclude-select-options
                      :excluded-options="excludedAmiLevels"
                      :included-options="includedAmiLevels"
                      :options-function="getAmiLevelsList"
                      label="AMI Levels"
                      title="AMI Levels"
                      use-filter-help-text-slots
                      @apply="handleAmiLevelApply"
                    >
                      <template #filter-help-text>
                        Filter units based on affordability thresholds tied to a percentage of the
                        local Area Median Income (AMI).
                      </template>
                    </include-exclude-select-options>
                  </q-item-section>
                </q-item>
                <q-item>
                  <q-item-section>
                    <date-range-picker
                      id="intercom-toolbar-2-date-filter"
                      :date-range="dateRange"
                      :default-date-range="defaultDateRange"
                      :dense="!denseDateRangePicker"
                      show-set-default
                      title="Date Availability"
                      @update="changedDateRange"
                      @set-default="setDefaultDateRange"
                    />
                  </q-item-section>
                </q-item>
              </q-list>
            </q-btn-dropdown>
          </div>
        </div>
      </div>
    </q-toolbar>
  </safe-teleport>
  <q-dialog
    v-if="showExcelTesting"
    v-model="showExcelTesting"
    transition-duration="100"
    @close="closeExcelTesting()"
  >
    <excel-testing v-if="excelTestingPayload !== null" :excel-payload="excelTestingPayload" />
    <q-linear-progress v-else indeterminate />
  </q-dialog>
  <q-dialog v-if="hasAPI" v-model="showApiDialog" transition-duration="100">
    <q-card style="width: 600px; max-width: 80vw">
      <q-card-section>
        <div class="text-bold">Pull Property Data with API</div>
      </q-card-section>
      <q-separator />
      <q-card-section class="row q-col-gutter-md">
        <div class="col-12 col-sm-4">
          <div class="text-subtitle2 q-mb-sm">Select Property</div>
          <q-scroll-area style="height: 150px; border: 1px solid #ddd; border-radius: 4px">
            <q-list dense>
              <q-item
                v-for="prop in apiProperties"
                :key="prop.value"
                v-ripple
                :active="selectedApiProperty === prop.value"
                clickable
                @click="selectedApiProperty = prop.value"
              >
                <q-item-section>
                  {{ prop.label }}
                </q-item-section>
              </q-item>
            </q-list>
          </q-scroll-area>
        </div>
        <div class="col-12 col-sm-8">
          <p class="text-subtitle2 q-mb-sm">CURL Command</p>
          <q-input v-model="placeholderCurl" filled readonly type="textarea" />
          <div class="row q-mt-sm">
            <q-btn
              v-if="selectedApiProperty"
              outline
              size="sm"
              @click="copyToClipboard(placeholderCurl)"
            >
              <q-icon class="q-mr-sm" name="content_copy" ripple size="xs" />
              Copy
            </q-btn>
          </div>
        </div>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn v-close-popup color="primary" flat label="Close" />
      </q-card-actions>
    </q-card>
  </q-dialog>
  <q-dialog v-model="showPdfDialog" transition-duration="100">
    <q-card flat style="width: 400px">
      <q-card-section>
        <div class="modal-title q-mb-xs">PDF Export</div>
        <div class="text-grey-7">Sections to include in the PDF export</div>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <q-tree
          v-model:ticked="ticked"
          :nodes="filteredReportSections"
          node-key="name"
          tick-strategy="leaf"
        />
        <q-banner class="q-mt-md text-green-7 bg-green-1 round items-center" dense inline rounded>
          <div class="text-caption">
            <q-icon class="q-mr-sm" name="info" />Current filters will be applied to the PDF export.
          </div>
        </q-banner>
        <div class="column q-mt-md">
          <q-toggle
            v-if="!useNewPdfDownload"
            v-model="pdfHighQuality"
            class="q-mb-md q-ml-sm"
            color="primary"
            dense
            label="High Quality (slower)"
          />
          <div
            :class="{
              active: useNewPdfDownload
            }"
            class="fancy-feature-box q-pa-sm cursor-pointer"
            @click="useNewPdfDownload = !useNewPdfDownload"
          >
            <div class="row items-center no-wrap">
              <q-toggle v-model="useNewPdfDownload" class="q-mr-sm" color="primary" dense />
              <div class="column">
                <div class="row items-center">
                  <span class="text-weight-medium text-primary"> New PDF Export </span>
                  <q-badge
                    class="q-px-xs q-ml-xs"
                    color="primary-light"
                    outline
                    style="font-size: 8px"
                  >
                    BETA
                  </q-badge>
                </div>
                <span
                  :class="{
                    'text-grey-8': !$q.dark.isActive,
                    'text-grey-5': $q.dark.isActive
                  }"
                  class="text-caption"
                >
                  Our new PDF export system focused on higher quality and improved formatting.
                </span>
              </div>
              <q-space />
              <q-icon
                v-if="$q.screen.gt.sm"
                :color="useNewPdfDownload ? 'primary-light' : 'grey-6'"
                class="q-ml-sm"
                name="auto_awesome"
                size="sm"
              />
            </div>
          </div>
          <q-banner
            v-if="isSafariBrowser && useNewPdfDownload"
            class="q-mt-md text-orange-8 bg-orange-1 round items-center"
            dense
            inline
            rounded
          >
            <div class="text-caption">
              <q-icon class="q-mr-sm" name="warning" />
              Safari users have reported issues with the new report system. We are working on the
              issue. In the meantime, try using Chrome or Firefox if you're experiencing errors when
              downloading the report in Safari.
            </div>
          </q-banner>
        </div>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn flat label="Cancel" no-caps @click="showPdfDialog = false" />
        <q-btn
          :disable="ticked.length === 0"
          :loading="downloadPdfLoading"
          color="primary"
          label="Download PDF"
          no-caps
          type="submit"
          unelevated
          @click="downloadPdf()"
        />
        <q-btn
          v-if="isAsyncPdfDownloadVisible"
          :loading="downloadPdfLoading"
          color="secondary"
          label="Async (Beta)"
          no-caps
          type="submit"
          unelevated
          @click="downloadPdf('async')"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
  <generate-report-dialog
    v-if="showReportDialog"
    v-model="showReportDialog"
    :comparable-details="comparableDetails"
    :expense-benchmark-data="expenseBenchmarkData"
    :has-financial-analysis="hasFinancialAnalysis"
    :map-snapshot-url="mapSnapshotUrl"
    :map-snapshot-alternative-url="mapSnapshotAlternativeUrl"
    :report-name="report?.name"
    :similarities="similarities"
    :subject-detail="subjectDetail"
  />
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { format, addDays, parseISO, max, formatDistance } from 'date-fns'

declare const Intercom: (action: string, ...args: any[]) => void

import {
  defaultColumns,
  defaultSections,
  excelPayload,
  expandUnitHistory,
  getBestLeaseTermPlan,
  getLatestPricesByFilter,
  getStreetViewUrl,
  getUnitsByFilter,
  getUnitTypes,
  overwritePropertyData,
  Section,
  SectionColumn,
  type Availability,
  type AvailabilityPeriod,
  type Price,
  type PropertyComparableResponse,
  type PropertyDetailsResponse,
  type PropertySearchResponse,
  type PropertyReportSubscriptionAttributes,
  type Similarities,
  type UnitType,
  UnitAmenityTag,
  type PricingSettings,
  ListingStatus,
  maxCompLimit,
  getAmenityTitle,
  type GlobalFilterValues,
  type Option,
  type Polygons,
  type AmiLevel,
  type AmenitySelections,
  isUnitAMIAffordable,
  OrganizationVisibility
} from 'common'

import {
  unitTypeColor,
  formatNumber,
  type TabName,
  getApplicationTabs,
  getVisibleColumnsForSection,
  getToggleableColumnsForSection
} from '@/utils'

import ButtonShare from '@/components/ButtonShare.vue'
import DateRangePicker, { presetToRange, type PresetRange } from '@/components/DateRangePicker.vue'
import PropertyCompareAmenities from './PropertyCompareAmenities.vue'
import PropertyCompareBubbleChart from './PropertyCompareBubbleChart.vue'
import PropertyCompareBuildingInfo from './PropertyCompareBuildingInfo.vue'
import PropertyCompareConcessions from './PropertyCompareConcessions.vue'
import PropertyCompareEstimatedRentRoll from './PropertyCompareEstimatedRentRoll.vue'
import PropertyCompareFees from './PropertyCompareFees.vue'
import PropertyCompareFinancialAnalysis from './PropertyCompareFinancialAnalysis.vue'
import PropertyCompareFinishes from './PropertyCompareFinishes.vue'
import PropertyCompareFutureAvailability from './PropertyCompareFutureAvailability.vue'
import PropertyCompareGoogleMap from './PropertyCompareGoogleMap.vue'
import PropertyCompareMarketAlerts from './PropertyCompareMarketAlerts.vue'
import PropertyComparePriceRecommendation from './PropertyComparePriceRecommendation.vue'
import PropertyCompareRentTrends from './PropertyCompareRentTrends.vue'
import PropertyCompareUnitTypeTable from './PropertyCompareUnitTypeTable.vue'
import PropertyCompareUnitDetails from './PropertyCompareUnitDetails.vue'
import PropertyCompareUnitMetrics from './PropertyCompareUnitMetrics.vue'
import PropertyCompareUnitTypeMetrics from './PropertyCompareUnitTypeMetrics.vue'
import PropertyCompareVolumeOverTime from './PropertyCompareVolumeOverTime.vue'
import PropertyCompareRecommendationHighlights from './PropertyCompareRecommendationHighlights.vue'
import PropertyCompareOverview from './PropertyCompareOverview.vue'
import PropertyCompareDebug from './PropertyCompareDebug.vue'
import PropertyCompareLeaseTerms from './PropertyCompareLeaseTerms.vue'
import SelectOptions from '@/components/SelectOptions.vue'

import PropertyCompareUnitPricesOverTime from './PropertyCompareUnitPricesOverTime.vue'
import PropertyCompareOccupancyChart from './PropertyCompareOccupancyChart.vue'
import PropertyCompareOccupancyProjections from './PropertyCompareOccupancyProjections.vue'
import PropertyCompareTaxHistory from './PropertyCompareTaxHistory.vue'
import PropertyCompareRentProjections from './PropertyCompareRentProjections.vue'
import PropertyCompareLeasingVelocity from './PropertyCompareLeasingVelocity.vue'

import templater from '@/api/templater'
import report from '@/api/report'

import { defineAsyncComponent } from 'vue'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { usePropertyStore } from '@/stores/property'
import { useStripeStore } from '@/stores/stripe'
import { useUserStore } from '@/stores/user'
import MinMaxFilter from '@/components/PropertyReports/MinMaxFilter.vue'
import IncludeExcludeSelectOptions from '@/components/IncludeExcludeSelectOptions.vue'
import LazyWrapper from '@/components/LazyWrapper.vue'
import { isLoaded, isNotStarted } from '@/utils/loading'
import WarningBanner from '@/components/WarningBanner.vue'
import PropertyCompareRankingsChart from '@/components/PropertyReports/PropertyCompareRankingsChart.vue'
import GenerateReportDialog from '@/components/GenerateReportDialog.vue'
import { type QNotifyUpdateOptions } from 'quasar'
import { useMarketAnalysisStore } from '@/stores'

import PropertyComparePoiGoogleMap from '@/components/PropertyReports/PropertyComparePoiGoogleMap.vue'
import useBackgroundTasksStore from '@/stores/backgroundtask'

// Legacy PDF: keep this import dynamic to avoid loading html2pdf in the initial bundle (it's huge)
const html2pdf = () => import('html2pdf.js')

interface PDF {
  internal: {
    getNumberOfPages: () => number
    pageSize: { getWidth: () => number; getHeight: () => number }
  }
  setPage(page: number): void
  setFontSize(size: number): void
  setTextColor(color: number): void
  text(text: string, x: number, y: number): void
  addImage(
    image: HTMLImageElement,
    type: string,
    x: number,
    y: number,
    width: number,
    height: number
  ): void
  extractImageFromDataUrl(dataUrl: string): [string, string]
  getStringUnitWidth(text: string): number
  link(x: number, y: number, width: number, height: number, options: { url: string }): void
}

interface FilteredUnit {
  unit: Availability
  lastHistory: Price
  period: AvailabilityPeriod
  prop: PropertyDetailsResponse
}

// Load some components dynamically to avoid loading them in the initial bundle

const ExcelTesting = defineAsyncComponent(() => import('@/components/ExcelTesting.vue'))
const PropertyCompareUnitMixRecommendation = defineAsyncComponent(
  () => import('@/components/PropertyReports/PropertyCompareUnitMixRecommendation.vue')
)

const tips: string[] = [
  'No need for new weekly reports - we update automatically. Just change the date range as needed.',
  'Use the Unit Amenity filter at the top to find affordable units easily.',
  'Hover over column names to see quick descriptions of each metric.',
  'Many values can be hovered for additional details. Give it a try!',
  'Collaborate with your team by sharing the report link.',
  'Toggle between Effective and Asking Rent to see how concessions impact numbers.',
  'Update property details like unit count and amenities using the Edit button.',
  'When editing, reorder comparables using the drag handle to the left of the property image.',
  'Show rent Per Bed for individual properties in the Property Dropdown.',
  'Download a PDF of your report using the button in the top right.',
  "Easily adjust the date range you're viewing with the filter in the top right."
]

const isDetailedComps = (
  val: PropertySearchResponse | PropertyComparableResponse
): val is PropertyComparableResponse => {
  return Object.prototype.hasOwnProperty.call(val, 'similarity_score')
}

export default {
  name: 'PropertyCompare',
  components: {
    ButtonShare,
    DateRangePicker,
    ExcelTesting,
    GenerateReportDialog,
    IncludeExcludeSelectOptions,
    LazyWrapper,
    MinMaxFilter,
    PropertyCompareAmenities,
    PropertyCompareBubbleChart,
    PropertyCompareBuildingInfo,
    PropertyCompareConcessions,
    PropertyCompareDebug,
    PropertyCompareEstimatedRentRoll,
    PropertyCompareFees,
    PropertyCompareFinancialAnalysis,
    PropertyCompareFinishes,
    PropertyCompareFutureAvailability,
    PropertyCompareGoogleMap,
    PropertyCompareLeaseTerms,
    PropertyCompareMarketAlerts,
    PropertyCompareOccupancyChart,
    PropertyCompareOccupancyProjections,
    PropertyCompareOverview,
    PropertyComparePriceRecommendation,
    PropertyCompareRankingsChart,
    PropertyCompareRecommendationHighlights,
    PropertyCompareRentProjections,
    PropertyCompareRentTrends,
    PropertyCompareUnitDetails,
    PropertyCompareUnitMetrics,
    PropertyCompareUnitMixRecommendation,
    PropertyCompareUnitPricesOverTime,
    PropertyCompareUnitTypeMetrics,
    PropertyCompareUnitTypeTable,
    PropertyCompareVolumeOverTime,
    PropertyCompareTaxHistory,
    PropertyCompareLeasingVelocity,
    PropertyComparePoiGoogleMap,
    SelectOptions,
    WarningBanner
  },
  props: {
    subject: {
      type: Object as PropType<PropertySearchResponse | null>,
      default: null
    },
    comparableAddresses: {
      type: Array<PropertySearchResponse | PropertyComparableResponse>,
      required: true
    },
    report: {
      type: Object as PropType<PropertyReportSubscriptionAttributes | null>,
      default: null
    },
    isSampleView: {
      type: Boolean,
      default: false
    },
    printingMode: {
      type: Boolean,
      default: false
    },
    globalFilterValues: {
      type: Object as PropType<GlobalFilterValues | null>,
      default: null
    },
    passedInTickedValues: {
      type: Array as PropType<Section[]>,
      default: () => []
    },
    passedInComponentOptions: {
      type: Object as PropType<{
        amenitySelections: AmenitySelections
      } | null>,
      default: null
    },
    hideLegacyPrintingHeader: {
      type: Boolean,
      default: false
    }
  },
  emits: ['togglePrintMode'],
  data() {
    return {
      maxCompLimit,
      Section,
      defaultSections,
      defaultColumns,
      expandedUnitHistory: {} as { [unit_id: string]: { created_on: string; history: Price }[] },
      localStorageExportSettingsKey: 'pdf-section-preferences',
      pricePerBed: {} as { [propId: string]: boolean },
      streetviewError: false,
      loading: false,
      downloadExcelLoading: false,
      subjectDetail: null as PropertyDetailsResponse | null,
      comparableDetails: [] as PropertyDetailsResponse[],
      similarities: {} as { [key: string]: Similarities },
      progressTracker: null as { current: number; objective: number } | null,
      dateRange: { from: addDays(new Date(), -30), to: new Date() } as {
        from: Date
        to: Date
      },
      listingsSelection: [
        { label: 'Available', value: 'active' },
        { label: 'Leased', value: 'closed' }
      ] as {
        label: string
        value: 'all' | 'active' | 'closed'
      }[],
      listingsOptions: [
        { label: 'Available', value: 'active' },
        { label: 'Leased', value: 'closed' }
      ] as { label: string; value: 'all' | 'active' | 'closed' }[],
      activeTab: 'property' as TabName,
      selectedUnitType: null as
        | {
            label: string
            value: { bed: number; bath: number; partial_bath: number | null }
          }[]
        | null,
      selectedBuildings: [] as { label: string; value: string }[],
      amiOptions: [
        {
          label: '30% AMI',
          value: 30
        },
        {
          label: '50% AMI',
          value: 50
        },
        {
          label: '60% AMI',
          value: 60
        },
        {
          label: '80% AMI',
          value: 80
        },
        {
          label: '120% AMI',
          value: 120
        }
      ] as { label: string; value: AmiLevel }[],
      includedAmiLevels: [] as { label: string; value: AmiLevel }[],
      excludedAmiLevels: [] as { label: string; value: AmiLevel }[],
      showPdfDialog: false,
      ticked: [Section.PROPERTY_GRID] as Section[],
      showApiDialog: false,
      selectedApiProperty: null as string | null,
      excelTestingPayload: null as Record<string, unknown> | null,
      showExcelTesting: false,
      sqftRange: { min: null, max: null } as { min: number | null; max: number | null },
      leaseTermSelection: [] as Option[],
      numCompsPerSegment: 10 as number,
      includedAmenities: [] as Option[],
      excludedAmenities: [] as Option[],
      handleMonitoringKeyboardShortcut: null as ((e: KeyboardEvent) => void) | null,
      showReportDialog: false,
      mapSnapshotUrl: '' as string,
      mapSnapshotAlternativeUrl: '' as string,
      useNewPdfDownload: true,
      pdfHighQuality: false, // Legacy PDF,
      defaultDateRange: 'last 30 days' as PresetRange,
      componentsToNotPrint: [
        Section.UNIT_DETAILS,
        Section.UNIT_METRICS,
        Section.RECOMMENDATION_HIGHLIGHTS,
        Section.UNIT_MIX_RECOMMENDATION,
        Section.FORECASTING
      ] as Section[],
      notificationDismissFunctions: [] as ((props?: QNotifyUpdateOptions) => void)[],
      componentOptions: {
        amenitySelections: {
          building_amenities: null,
          unit_amenities: null
        }
      } as {
        amenitySelections: AmenitySelections
      },
      propertiesSelectionColumns: [
        {
          label: 'Display',
          key: 'display'
        },
        {
          label: 'Per Bed',
          key: 'per-bed'
        }
      ]
    }
  },
  computed: {
    isAsyncPdfDownloadVisible(): boolean {
      const user = useUserStore().user
      if (user === undefined) {
        return false
      }

      return useUserStore().isHelloDataEmployee && user.jobTitle === 'Other'
    },
    hasMarketAnalytics() {
      return useStripeStore().hasMarketReports
    },
    denseDateRangePicker(): boolean {
      return this.$q.screen.gt.md
    },
    leaseTermSelectionSet(): Set<number | null> {
      return new Set(this.leaseTermSelection.map((_) => _.value))
    },
    expectedProperties(): number {
      if (this.report) {
        return Math.min(7, this.report.comparable_addresses.length + (this.report.subject ? 1 : 0))
      }
      return 5
    },
    progressPercentage(): number | null {
      if (this.progressTracker) {
        return this.progressTracker.current / this.progressTracker.objective
      }
      return null
    },
    hasAPI(): boolean {
      return useStripeStore().hasAPI
    },
    isAdmin() {
      return useUserStore().isAdmin
    },
    organization() {
      return useUserStore().organization
    },
    visibleSections() {
      if (this.isSampleView) {
        return defaultSections
      }
      return this.report?.sections ?? defaultSections
    },
    visibleColumns(): SectionColumn[] {
      if (this.isSampleView) {
        return defaultColumns
      }
      return this.report?.columns ?? defaultColumns
    },
    exportLogo() {
      return this.organization?.logo_url ?? '/hd-logo.webp'
    },
    isHelloDataEmployee() {
      return useUserStore().isHelloDataEmployee
    },
    reportInOrg() {
      const report = this.report
      if (report?.user_id) {
        return useUserStore().reportInOrg(report)
      }
      return false
    },
    applicationTabs() {
      return getApplicationTabs()
    },
    tabs(): {
      displayed: boolean | undefined
      disable: boolean
      name: TabName
      label: string
    }[] {
      const overviewTab = this.applicationTabs.find((tab) => tab.name === 'overview')
      const propertyTab = this.applicationTabs.find((tab) => tab.name === 'property')
      const unitTab = this.applicationTabs.find((tab) => tab.name === 'unit')
      const feesAndAmenitiesTab = this.applicationTabs.find(
        (tab) => tab.name === 'feesAndAmenities'
      )
      const expenseBenchmarksTab = this.applicationTabs.find(
        (tab) => tab.name === 'expenseBenchmarks'
      )
      const revenueManagementTab = this.applicationTabs.find(
        (tab) => tab.name === 'revenueManagement'
      )
      return [
        {
          label: 'Overview',
          name: 'overview' as TabName,
          disable: this.loading,
          displayed:
            overviewTab &&
            overviewTab.components.some(
              (_) => this.visibleSections && this.visibleSections.includes(_.name)
            )
        },
        {
          label: 'Rent Comps',
          name: 'property' as TabName,
          disable: this.loading,
          displayed:
            propertyTab &&
            propertyTab.components.some(
              (_) => this.visibleSections && this.visibleSections.includes(_.name)
            )
        },
        {
          label: 'Unit-Level Data',
          name: 'unit' as TabName,
          disable: this.loading,
          displayed:
            unitTab &&
            unitTab.components.some(
              (_) => this.visibleSections && this.visibleSections.includes(_.name)
            )
        },
        {
          label: 'Fees & Amenities',
          name: 'feesAndAmenities' as TabName,
          disable: this.loading,
          displayed:
            feesAndAmenitiesTab &&
            feesAndAmenitiesTab.components.some(
              (_) => this.visibleSections && this.visibleSections.includes(_.name)
            )
        },
        {
          label: 'Financial Analysis',
          name: 'expenseBenchmarks' as TabName,
          disable: !this.availableExpenseBenchmarks,
          displayed:
            expenseBenchmarksTab &&
            expenseBenchmarksTab.components.some(
              (_) => this.visibleSections && this.visibleSections.includes(_.name)
            ) &&
            (this.hasFinancialAnalysis || this.showExpenseBenchmarkUpsell || this.isSampleView)
        },
        {
          label: 'Pricing Analytics',
          name: 'revenueManagement' as TabName,
          disable: !this.subjectDetail || this.comparableDetails.length === 0, // disable if no subject or no comps
          displayed:
            revenueManagementTab &&
            revenueManagementTab.components.some(
              (_) => this.visibleSections && this.visibleSections.includes(_.name)
            ) &&
            this.hasRevManagementFeature
        }
      ].filter((_) => _.displayed)
    },
    listingStatusFilter(): 'active' | 'closed' | 'all' {
      if (this.listingsSelection.length !== 1) {
        return 'all'
      }

      return this.listingsSelection[0]?.value ?? 'all'
    },
    latestPricesByDateRangeOnly() {
      // use to count the number of units for each unit type and building in the toolbar only
      return this.allProperties.map((prop) => {
        return {
          prop,
          units: getLatestPricesByFilter(prop, this.dateRange.from, this.dateRange.to)
        }
      })
    },
    filteredProperties() {
      // All global filters must be used here and only here.
      const utypes = this.selectedUnitType?.map((_) => _.value)
      const includedAmenities: UnitAmenityTag[] = this.includedAmenities.map((_) => _.value)
      const excludedAmenities: UnitAmenityTag[] = this.excludedAmenities.map((_) => _.value)
      const includedAmiLevels: AmiLevel[] = this.includedAmiLevels.map((_) => _.value)
      const excludedAmiLevels: AmiLevel[] = this.excludedAmiLevels.map((_) => _.value)
      const leaseTerms: Set<number | null> = this.leaseTermSelectionSet

      return this.allProperties.flatMap((prop) => {
        if (!this.selectedBuildings.some((_) => _.value === prop.id)) {
          return []
        }
        return {
          prop,
          units: getUnitsByFilter(
            prop,
            getLatestPricesByFilter(
              prop,
              this.dateRange.from,
              this.dateRange.to,
              this.sqftRange.min,
              this.sqftRange.max,
              utypes,
              includedAmenities,
              excludedAmenities,
              includedAmiLevels,
              excludedAmiLevels
            ),
            this.dateRange.to,
            this.listingStatusFilter
          )
            .filter(({ lastHistory }) => {
              return (
                !leaseTerms.size ||
                (leaseTerms.has(null) && !lastHistory.price_plans?.length) ||
                lastHistory.price_plans?.some((p) => leaseTerms.has(p[0]))
              )
            })
            .map((unit) => this.withBestLeaseTerm({ ...unit, prop }, leaseTerms))
        }
      })
    },
    filteredPropertiesWithUnitProps() {
      return this.filteredProperties.flatMap(({ prop, units }) =>
        units.map(({ unit, lastHistory, period }) => ({ prop, unit, lastHistory, period }))
      )
    },
    filteredReportSections() {
      // use the feature flag to filter out sections (or components of sections) that are not enabled
      // also filter out sections where all components are turned off in admin settings
      return this.applicationTabs.flatMap((section) => {
        if (
          !section.authorized ||
          !section.components.some((_) => this.visibleSections?.includes(_.name)) ||
          section.name === 'revenueManagement'
        ) {
          return []
        }

        return {
          ...section,
          children: section.components.filter((child) => {
            if (
              !child.authorized ||
              !this.visibleSections?.includes(child.name) ||
              this.componentsToNotPrint.includes(child.name)
            ) {
              return false
            }
            return true
          })
        }
      })
    },
    lastUpdated() {
      if (this.allProperties.length) {
        return formatDistance(
          new Date(),
          max(this.allProperties.map((_) => parseISO(_.created_on)))
        )
      }
      return null
    },
    getStreetViewUrlUnion() {
      if (this.subject === null) {
        return null
      }
      const userEdits = this.report?.property_edits?.find((_) => _.property_id === this.subject?.id)
      if (userEdits?.street_view_url) {
        return userEdits.street_view_url
      }
      const street_address = userEdits?.street_address || this.subject.street_address
      const { city, state, zip_code } = this.subject
      const address = [street_address, city, state, zip_code].join(', ')
      if (this.streetviewError) {
        return getStreetViewUrl({ ...this.subject, street_address }, import.meta.env.VITE_API_URL)
      }
      return `https://storage.googleapis.com/hello-data-ai-app-frontend/streetviews/${address}.jpeg`
    },
    pageName() {
      return (
        this.report?.name ??
        this.subject?.building_name ??
        this.subject?.street_address ??
        'Property Report'
      )
    },
    hasPropertyReports() {
      return useStripeStore().hasPropertyReports
    },
    billedPerSeat() {
      return useStripeStore().billedPerSeat
    },
    usesSSO(): boolean {
      return this.organization?.connection_id ? true : false
    },
    hasFinancialAnalysis(): boolean {
      return useStripeStore().hasFinancialAnalysis
    },
    expenseBenchmarkData() {
      const expenseBenchmark = usePropertyReportStore().expenseBenchmark
      return isLoaded(expenseBenchmark) ? expenseBenchmark.data : null
    },
    showExpenseBenchmarkUpsell() {
      if (this.isSampleView || this.hasFinancialAnalysis || this.usesSSO) {
        return false
      }
      if (this.billedPerSeat) {
        return true
      }
      return this.isAdmin
    },
    hasRevManagementFeature() {
      return useStripeStore().hasRevenueManagement
    },
    hasRentProjections() {
      return useStripeStore().hasRentProjections
    },
    allProperties() {
      if (this.subjectDetail) {
        return [this.subjectDetail, ...this.comparableDetails]
      }
      return this.comparableDetails
    },
    apiProperties() {
      return this.allProperties.map((prop) => ({
        label: prop.building_name || prop.street_address,
        value: prop.id
      }))
    },
    placeholderCurl() {
      if (!this.selectedApiProperty) {
        return 'Select a property to see the CURL command'
      }
      return `curl https://api.hellodata.ai/property/${this.selectedApiProperty} -H "x-api-key: <apiKey>"`
    },
    isNewDevelopment() {
      return this.subjectDetail?.user_id !== undefined && this.subjectDetail.user_id !== null
    },
    segmentedFilteredProperties() {
      // no need to do anything if there are less than N comps + subject
      if (this.filteredProperties.length <= this.numCompsPerSegment) {
        return [this.filteredProperties]
      }
      // this is used for the overview grid when printing
      // we want segments of subject + 10 comps
      const subjectIdx = this.filteredProperties.findIndex(
        (prop) => prop.prop.id === this.subjectDetail?.id
      )
      const filteredPropsWithoutSubject =
        subjectIdx >= 0
          ? [
              ...this.filteredProperties.slice(0, subjectIdx),
              ...this.filteredProperties.slice(subjectIdx + 1)
            ]
          : this.filteredProperties
      const numSegments = Math.ceil(filteredPropsWithoutSubject.length / this.numCompsPerSegment)
      const segments = []
      if (numSegments === 0) {
        return [[this.filteredProperties[0]]]
      }
      for (let i = 0; i < numSegments; i++) {
        if (subjectIdx >= 0) {
          // each segment should have the subject first
          segments.push([
            this.filteredProperties[subjectIdx],
            ...filteredPropsWithoutSubject.slice(
              this.numCompsPerSegment * i,
              this.numCompsPerSegment * (i + 1)
            )
          ])
        } else {
          segments.push([
            ...filteredPropsWithoutSubject.slice(
              this.numCompsPerSegment * i,
              this.numCompsPerSegment * (i + 1)
            )
          ])
        }
      }
      return segments
    },
    /**
     * Return false if the subject is missing necessary data to computed an expense benchmark
     */
    availableExpenseBenchmarks() {
      const countValidUnits = this.subjectDetail?.building_availability
        .map((_) => _.price || _.min_price || _.max_price)
        .filter((_) => _).length

      return this.subjectDetail?.number_units && countValidUnits
    },
    selectedUnitTypeValues() {
      if (!this.selectedUnitType) {
        return []
      }
      return this.selectedUnitType.map((_) => _.value)
    },
    downloadPdfLoading() {
      return usePropertyReportStore().getDownloadPdfLoading
    },
    isSafariBrowser(): boolean {
      const userAgent = window.navigator.userAgent
      return userAgent.indexOf('Safari') > -1 && userAgent.indexOf('Chrome') <= -1
    },
    canEditPropertyReports() {
      return useUserStore().canEditPropertyReports
    },
    userHasReadOnlyAccess() {
      if (!this.report || !useUserStore().user?.sub) {
        return false
      }

      const currentUserId = useUserStore()?.user?.sub
      // if it's the report owner, they should never have read only access
      if (this.report.user_id === currentUserId) {
        return false
      }

      const accessUsers = this.report.access_users || []

      const currentUserAccess = accessUsers.find((user) => user.id === currentUserId)
      // if they are not in the access table, check the organization_visibility
      if (!currentUserAccess) {
        return this.report.organization_visibility === OrganizationVisibility.VIEWER
      }

      return currentUserAccess?.read_only === true
    },
    showReadOnlyView() {
      if (this.isAdmin) {
        return false
      }
      return !this.canEditPropertyReports || this.userHasReadOnlyAccess
    },
    financialAnalysisTooltip(): string | boolean {
      if (this.loading) {
        return false
      }

      if (!this.subjectDetail) {
        return 'Unavailable to reports without subject.'
      }
      if (!this.availableExpenseBenchmarks) {
        return 'Not enough unit data found with current filters.'
      }

      return false
    },
    perBedAllChecked() {
      if (this.allProperties.length === 0) {
        return false
      }
      for (const prop of this.allProperties) {
        if (!this.pricePerBed[prop.id]) {
          return false
        }
      }
      return true
    }
  },
  watch: {
    activeTab() {
      const el = document.getElementById('scroll-anchor')
      if (el) {
        el.scrollIntoView({ block: 'start' })
      }
    },
    leaseTermSelection: {
      deep: true,
      handler() {
        if (this.allProperties.length) {
          this.expandedUnitHistory = this.computeExpandedUnitHistory()
        }
      }
    }
  },
  created() {
    // load the subject and comparable details
    this.loading = true
    let subjectPromise = Promise.resolve(null) as Promise<PropertyDetailsResponse | null>

    // load the default date range
    const defaultDateRange = localStorage.getItem('defaultDateRange')
    if (defaultDateRange) {
      this.dateRange = presetToRange(defaultDateRange as PresetRange)
      this.defaultDateRange = defaultDateRange as PresetRange
    }

    const numPropertiesBeingLoaded =
      this.comparableAddresses.length > maxCompLimit
        ? maxCompLimit + (this.subject ? 1 : 0)
        : this.comparableAddresses.length + (this.subject ? 1 : 0)
    this.progressTracker = {
      current: 0,
      objective: numPropertiesBeingLoaded
    }

    if (this.subject) {
      subjectPromise = usePropertyStore()
        .details(this.subject.id)
        .then((subject) => {
          if (this.progressTracker) {
            this.progressTracker.current++
          }
          subject = overwritePropertyData(subject, this.report?.property_edits ?? [])

          const numberComps = this.comparableAddresses.length ?? 0
          const completeComps = this.comparableAddresses.some(isDetailedComps)
          const newSims: { [key: string]: Similarities } = {}
          if (completeComps) {
            for (const comp of this.comparableAddresses) {
              if (isDetailedComps(comp)) {
                newSims[comp.id] = comp.similarity_score
              }
            }
            this.similarities = newSims
          } else if (numberComps > 0) {
            usePropertyStore()
              .comparables(subject, {
                excludedIds: this.report?.excluded_property_ids ?? [],
                selectedIds: this.comparableAddresses.map((_) => _.id).slice(0, this.maxCompLimit),
                topN: numberComps > this.maxCompLimit ? this.maxCompLimit : numberComps
              })
              .then((resp) => {
                resp.comparables.forEach((comp) => {
                  newSims[comp.id] = comp.similarity_score
                })
                this.similarities = newSims
              })
              .catch((e) => {
                console.error('An error occurred while loading comparables', e)
                this.$q.notify({
                  message: 'An error occurred while loading comparables',
                  color: 'negative',
                  position: 'top'
                })
                return []
              })
          }
          return subject
        })
        .catch((e) => {
          console.error('An error occurred while loading the subject property', e)
          this.$q.notify({
            message: 'An error occurred while loading the subject property',
            color: 'negative',
            position: 'top'
          })
          return null
        })
    }

    const comparableIds = this.comparableAddresses.slice(0, this.maxCompLimit).map((_) => _.id)
    const compsPromise = usePropertyStore()
      .detailsByIds(comparableIds)
      .catch((e) => {
        console.error('An error occurred while loading comparable properties', e)
        this.$q.notify({
          message: 'An error occurred while loading comparable properties',
          color: 'negative',
          position: 'top'
        })
        return [] as PropertyDetailsResponse[]
      })
      .finally(() => {
        if (this.progressTracker) {
          this.progressTracker.current += comparableIds.length
        }
      })

    Promise.all([subjectPromise, compsPromise])
      .then(([subjectDetail, compsDetails]) => {
        if (subjectDetail !== null) {
          subjectDetail = overwritePropertyData(subjectDetail, this.report?.property_edits ?? [])
        }

        compsDetails.forEach((comp, idx) => {
          if (comp === null || comp === undefined) {
            console.error(
              `Property is null or undefined. Should be ${this.comparableAddresses[idx]} instead.`
            )
          }
          overwritePropertyData(comp, this.report?.property_edits ?? [])
        })

        // Use nextTick to batch these updates together and avoid unnecessary re-renders
        this.$nextTick(() => {
          this.subjectDetail = subjectDetail
          this.comparableDetails = compsDetails

          if (!this.printingMode) {
            this.selectedBuildings = [subjectDetail, ...compsDetails].flatMap((_) => {
              if (_ === null) {
                return []
              }
              return {
                label: _.building_name || _.street_address,
                value: _.id
              }
            })
          }

          // using a data property here to avoid the cost of tracking with a reactive property of a computed property
          this.expandedUnitHistory = this.computeExpandedUnitHistory()
        })
      })
      .catch((e) => {
        console.error('An error occurred while loading the property report', e)
        this.$q.notify({
          message: 'An error occurred while loading the property report',
          color: 'negative',
          position: 'top'
        })
      })
      .finally(() => {
        this.loading = false
        this.progressTracker = null
      })
    const propertyTab = this.tabs.find((tab) => tab.name === 'property')
    // check to see if property tab has any visible sections
    if (propertyTab?.displayed) {
      this.activeTab = propertyTab.name
    } else {
      // if not, make first tab with a visible section the default active tab
      this.tabs.some((tab) => {
        if (tab.displayed) {
          this.activeTab = tab.name
          return true
        }
      })
      if (this.activeTab === null) {
        this.$q.notify({
          message: 'Error finding an application tab with a visible section.',
          color: 'negative',
          position: 'top'
        })
        console.error('Could not find a tab with a visible section.')
      }
    }
  },
  mounted() {
    if (this.globalFilterValues) {
      this.dateRange = this.globalFilterValues.dateRange
      this.listingsSelection = this.globalFilterValues.listingsSelection
      this.selectedUnitType = this.globalFilterValues.selectedUnitType
      this.selectedBuildings = this.globalFilterValues.selectedBuildings
      this.sqftRange = this.globalFilterValues.sqftRange
      this.leaseTermSelection = this.globalFilterValues.leaseTermSelection
      this.includedAmenities = this.globalFilterValues.includedAmenities
      this.excludedAmenities = this.globalFilterValues.excludedAmenities
      this.includedAmiLevels = this.globalFilterValues.includedAmiLevels
      this.excludedAmiLevels = this.globalFilterValues.excludedAmiLevels
    }

    if (this.passedInTickedValues.length) {
      this.ticked = this.passedInTickedValues
    } else {
      const savedTicked = localStorage.getItem(this.localStorageExportSettingsKey)
      try {
        const parsedTicked = savedTicked
          ? (JSON.parse(savedTicked) as Section[])
          : ([] as Section[])
        this.ticked =
          Array.isArray(parsedTicked) && parsedTicked.every((item) => typeof item === 'string')
            ? parsedTicked
            : this.applicationTabs.flatMap((_) => _.components.map((_) => _.name))
      } catch (e) {
        console.error('Failed to parse saved PDF export settings:', e)
        this.ticked = this.applicationTabs.flatMap((_) => _.components.map((_) => _.name))
      }
    }

    if (this.passedInComponentOptions) {
      // for printing, we want to use the passed in component options
      this.componentOptions = this.passedInComponentOptions
    }

    if (this.isHelloDataEmployee) {
      this.handleMonitoringKeyboardShortcut = (e) => {
        if (e.key === 'D' && e.shiftKey) {
          this.activeTab = 'debug'
        }
      }
      window.addEventListener('keydown', this.handleMonitoringKeyboardShortcut)
    }
  },
  unmounted() {
    if (this.handleMonitoringKeyboardShortcut) {
      window.removeEventListener('keydown', this.handleMonitoringKeyboardShortcut)
    }
    this.notificationDismissFunctions.forEach((fnc) => fnc())
  },
  methods: {
    getToggleableColumnsForSection,
    getVisibleColumnsForSection,
    formatNumber,
    format,
    formatDistance,
    parseISO,
    handleBuildingChange(val: { label: string; value: string }) {
      this.$tracker.changedFilter('properties', 'toolbar', val)
    },
    async seeInMarketAnalytics() {
      try {
        // Check if we have a subject property
        if (!this.subjectDetail) {
          this.$q.notify({
            type: 'negative',
            message: 'No subject property found'
          })
          return
        }

        // Show loading notification
        this.$q.notify({
          type: 'info',
          message: 'Finding similar market...',
          timeout: 2000
        })

        // Get top 100 comparables for the subject property
        const comparablesResponse = await usePropertyStore().comparables(this.subjectDetail, {
          excludedIds: this.report?.excluded_property_ids ?? [],
          selectedIds: [],
          topN: 50,
          maxDistance: 50, // 50 miles radius
          useEmbedding: true
        })

        // Extract properties with valid coordinates
        const allProperties = [this.subjectDetail, ...comparablesResponse.comparables]
        const propertiesWithCoords = allProperties.filter((prop) => prop.lat && prop.lon)

        if (propertiesWithCoords.length === 0) {
          this.$q.notify({
            type: 'negative',
            message: 'No properties with valid coordinates found'
          })
          return
        }

        // remove outliers based on statistical analysis
        let properties = this.removeOutliers(propertiesWithCoords)

        if (properties.length === 0) {
          // let's not remove any properties if we don't have any left
          properties = propertiesWithCoords
        }

        // Create coordinates array
        const coordinates = properties.map((prop) => [prop.lon!, prop.lat!])

        let polygon: Polygons

        if (coordinates.length === 1) {
          // Single property - create a small radius around it
          const [lon, lat] = coordinates[0]
          const radius = 0.01 // approximately 0.7 miles
          polygon = [
            [
              [
                [lon - radius, lat - radius],
                [lon + radius, lat - radius],
                [lon + radius, lat + radius],
                [lon - radius, lat + radius],
                [lon - radius, lat - radius]
              ]
            ]
          ]
        } else {
          // Multiple properties - try to get census tract boundary, fallback to convex hull
          try {
            const marketAnalyticsApi = (await import('@/api/marketanalytics')).default
            const censusTractPolygon = await marketAnalyticsApi.boundaryFromComps(coordinates)

            if (censusTractPolygon && censusTractPolygon.length > 0) {
              polygon = censusTractPolygon
            } else {
              // Fallback to convex hull if census tract boundary not available
              console.log('Census tract boundary not available, using convex hull fallback')
              polygon = this.createConvexHull(coordinates)
            }
          } catch (error) {
            console.error('Error getting census tract boundary:', error)
            // Fallback to convex hull on error
            polygon = this.createConvexHull(coordinates)
          }
        }

        // Generate report name
        const subjectName =
          this.subjectDetail?.building_name || this.subjectDetail?.street_address || 'Property'
        const reportName = `${subjectName} Market`

        // Calculate filter ranges from comparables
        const yearBuiltValues = properties
          .map((p) => p.year_built)
          .filter((year) => year !== null && year !== undefined)

        const numberUnitsValues = properties
          .map((p) => p.number_units)
          .filter((units) => units !== null && units !== undefined)

        const numberStoriesValues = properties
          .map((p) => p.number_stories)
          .filter((stories) => stories !== null && stories !== undefined)

        // Log filter initialization info
        console.log('Initializing market report filters:', {
          totalProperties: properties.length,
          yearBuiltRange:
            yearBuiltValues.length > 0
              ? `${Math.min(...yearBuiltValues)}-${Math.max(...yearBuiltValues)}`
              : 'N/A',
          unitsRange:
            numberUnitsValues.length > 0
              ? `${Math.min(...numberUnitsValues)}-${Math.max(...numberUnitsValues)}`
              : 'N/A',
          storiesRange:
            numberStoriesValues.length > 0
              ? `${Math.min(...numberStoriesValues)}-${Math.max(...numberStoriesValues)}`
              : 'N/A'
        })

        // Create market report with initialized filters
        const marketReport = await useMarketAnalysisStore().create({
          name: reportName,
          filters: {
            coordinates: polygon,
            year_built:
              yearBuiltValues.length > 0
                ? {
                    min: Math.min(...yearBuiltValues),
                    max: Math.max(...yearBuiltValues),
                    allow_null: false
                  }
                : undefined,
            number_units:
              numberUnitsValues.length > 0
                ? {
                    min: Math.min(...numberUnitsValues),
                    max: Math.max(...numberUnitsValues),
                    allow_null: false
                  }
                : undefined,
            number_stories:
              numberStoriesValues.length > 0
                ? {
                    min: Math.min(...numberStoriesValues),
                    max: Math.max(...numberStoriesValues),
                    allow_null: false
                  }
                : undefined
          },
          id: -1,
          user_id: useUserStore().user?.sub || '',
          created_on: new Date().toISOString(),
          modified_on: new Date().toISOString(),
          deleted_on: null
        })

        if (marketReport) {
          // Navigate to the market report
          this.$router.push({
            name: 'Market Analysis',
            params: { reportId: marketReport.id.toString() }
          })

          this.$q.notify({
            type: 'positive',
            message: 'Market analytics report created successfully',
            timeout: 5000
          })
        } else {
          this.$q.notify({
            type: 'negative',
            message: 'Failed to create market analytics report'
          })
        }
      } catch (error) {
        console.error('Error creating market analytics:', error)
        this.$q.notify({
          type: 'negative',
          message: 'Error creating market analytics report'
        })
      }
    },
    async viewMarket(radiusOption: 'auto' | number) {
      try {
        // Check if we have a subject property
        if (!this.subjectDetail) {
          this.$q.notify({
            type: 'negative',
            message: 'No subject property found'
          })
          return
        }

        if (!this.subjectDetail.lat || !this.subjectDetail.lon) {
          this.$q.notify({
            type: 'negative',
            message: 'Subject property does not have valid coordinates'
          })
          return
        }

        let polygon: Polygons
        let reportName: string
        const subjectName =
          this.subjectDetail?.building_name || this.subjectDetail?.street_address || 'Property'

        if (radiusOption === 'auto') {
          // Use the existing seeInMarketAnalytics logic
          await this.seeInMarketAnalytics()
          return
        } else {
          // Create a circular polygon with the specified radius
          const radiusMiles = radiusOption
          polygon = this.createCirclePolygon(
            this.subjectDetail.lon,
            this.subjectDetail.lat,
            radiusMiles
          )
          reportName = `${subjectName} Market (${radiusMiles}mi Radius)`
        }

        // Create market report with the polygon
        const marketReport = await useMarketAnalysisStore().create({
          id: -1,
          name: reportName,
          filters: {
            coordinates: polygon
          },
          user_id: useUserStore().user?.sub || '',
          created_on: new Date().toISOString(),
          modified_on: new Date().toISOString(),
          deleted_on: null
        })

        if (marketReport) {
          // Navigate to the market report
          this.$router.push({
            name: 'Market Analysis',
            params: { reportId: marketReport.id.toString() }
          })

          this.$q.notify({
            type: 'positive',
            message: `Market analytics report created successfully`,
            timeout: 5000
          })
        } else {
          this.$q.notify({
            type: 'negative',
            message: 'Failed to create market analytics report'
          })
        }
      } catch (error) {
        console.error('Error creating market analytics:', error)
        this.$q.notify({
          type: 'negative',
          message: 'Error creating market analytics report'
        })
      }
    },
    createCirclePolygon(lon: number, lat: number, radiusMiles: number): Polygons {
      // Convert miles to meters
      const radiusMeters = radiusMiles * 1609.34
      const earthRadius = 6371000 // Earth radius in meters
      const points: [number, number][] = []

      // Generate 360 points to form a circle
      for (let i = 0; i < 360; i++) {
        const bearing = (i * Math.PI) / 180
        const lat1 = lat * (Math.PI / 180)
        const lon1 = lon * (Math.PI / 180)
        const lat2 = Math.asin(
          Math.sin(lat1) * Math.cos(radiusMeters / earthRadius) +
            Math.cos(lat1) * Math.sin(radiusMeters / earthRadius) * Math.cos(bearing)
        )
        const lon2 =
          lon1 +
          Math.atan2(
            Math.sin(bearing) * Math.sin(radiusMeters / earthRadius) * Math.cos(lat1),
            Math.cos(radiusMeters / earthRadius) - Math.sin(lat1) * Math.sin(lat2)
          )
        const latitude = lat2 * (180 / Math.PI)
        const longitude = lon2 * (180 / Math.PI)
        points.push([longitude, latitude])
      }
      // Close the polygon
      points.push(points[0])

      return [[points]]
    },
    createConvexHull(coordinates: number[][]): Polygons {
      if (coordinates.length <= 2) {
        // For 1-2 points, create a small rectangle around them
        const [lon, lat] = coordinates[0]
        const radius = 0.01
        return [
          [
            [
              [lon - radius, lat - radius],
              [lon + radius, lat - radius],
              [lon + radius, lat + radius],
              [lon - radius, lat + radius],
              [lon - radius, lat - radius]
            ]
          ]
        ]
      }

      // Simple convex hull algorithm (Graham scan)
      const points = coordinates.map(([lon, lat], index) => ({ lon, lat, index }))

      // Find the bottom-most point (and leftmost in case of tie)
      let bottom = points[0]
      for (const point of points) {
        if (point.lat < bottom.lat || (point.lat === bottom.lat && point.lon < bottom.lon)) {
          bottom = point
        }
      }

      // Sort points by polar angle with respect to bottom point
      const sortedPoints = points
        .filter((p) => p !== bottom)
        .sort((a, b) => {
          const angleA = Math.atan2(a.lat - bottom.lat, a.lon - bottom.lon)
          const angleB = Math.atan2(b.lat - bottom.lat, b.lon - bottom.lon)
          return angleA - angleB
        })

      // Build convex hull
      const hull = [bottom, ...sortedPoints]
      const stack = [hull[0], hull[1]]

      for (let i = 2; i < hull.length; i++) {
        while (
          stack.length > 1 &&
          this.crossProduct(stack[stack.length - 2], stack[stack.length - 1], hull[i]) <= 0
        ) {
          stack.pop()
        }
        stack.push(hull[i])
      }

      // Convert to polygon format and close the polygon
      const polygon: number[][] = stack.map((point) => [point?.lon, point?.lat])
      polygon.push(polygon[0]) // Close the polygon

      return [[polygon]]
    },
    crossProduct(
      o: { lon: number; lat: number },
      a: { lon: number; lat: number },
      b: { lon: number; lat: number }
    ): number {
      return (a.lon - o.lon) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lon - o.lon)
    },
    removeOutliers(
      properties: (PropertyDetailsResponse | PropertyComparableResponse)[]
    ): (PropertyDetailsResponse | PropertyComparableResponse)[] {
      if (properties.length <= 3) {
        // Not enough properties to meaningfully detect outliers
        return properties
      }

      // Calculate mean and standard deviation for latitude and longitude
      const lats = properties.map((p) => p.lat!)
      const lons = properties.map((p) => p.lon!)

      const meanLat = lats.reduce((sum, lat) => sum + lat, 0) / lats.length
      const meanLon = lons.reduce((sum, lon) => sum + lon, 0) / lons.length

      const stdLat = Math.sqrt(
        lats.reduce((sum, lat) => sum + Math.pow(lat - meanLat, 2), 0) / lats.length
      )
      const stdLon = Math.sqrt(
        lons.reduce((sum, lon) => sum + Math.pow(lon - meanLon, 2), 0) / lons.length
      )

      if (stdLat === 0 || stdLon === 0) {
        return properties
      }

      // Define outlier threshold (2 standard deviations)
      const threshold = 2.0

      // Filter out outliers
      const filteredProperties = properties.filter((prop) => {
        const lat = prop.lat!
        const lon = prop.lon!

        // Check if property is within threshold standard deviations from mean
        const latZScore = Math.abs(lat - meanLat) / stdLat
        const lonZScore = Math.abs(lon - meanLon) / stdLon

        return latZScore <= threshold && lonZScore <= threshold
      })

      // Ensure we don't remove too many properties (keep at least 3)
      if (filteredProperties.length < 3) {
        // If we filtered too aggressively, use a more lenient threshold
        const lenientThreshold = 3.0
        return properties.filter((prop) => {
          const lat = prop.lat!
          const lon = prop.lon!

          const latZScore = Math.abs(lat - meanLat) / stdLat
          const lonZScore = Math.abs(lon - meanLon) / stdLon

          return latZScore <= lenientThreshold && lonZScore <= lenientThreshold
        })
      }

      return filteredProperties
    },
    getBuildingOptions() {
      return this.allProperties.map((prop) => {
        return {
          label: prop.building_name || prop.street_address,
          value: prop.id,
          count: this.latestPricesByDateRangeOnly.find((_) => _.prop.id === prop.id)?.units.length
        }
      })
    },
    getAmenitiesList() {
      const tags = {} as { [tag: string]: Option }
      for (const prop of this.latestPricesByDateRangeOnly) {
        for (const unit of prop.units) {
          if (!unit.unit.tags) {
            continue
          }
          for (const tag of unit.unit.tags) {
            // TODO: Better handling of Direction Views, ideally include direction in amenity tag
            if (tag !== 'view.direction') {
              // per Marc, exclude this tag from the filter
              const count = (tags[tag]?.count || 0) + 1
              tags[tag] = { value: tag, label: getAmenityTitle(tag), count: count }
            }
          }
        }
      }
      return Object.values(tags)
    },
    getAmiLevelsList() {
      const amiCounts = {} as { [level: number]: Option }
      // Initialize all AMI levels with 0 count
      for (const amiOption of this.amiOptions) {
        amiCounts[amiOption.value] = {
          value: amiOption.value,
          label: amiOption.label,
          count: 0
        }
      }

      // Count units that qualify for each AMI level
      for (const prop of this.latestPricesByDateRangeOnly) {
        for (const unit of prop.units) {
          for (const amiOption of this.amiOptions) {
            const minDateStr = format(this.dateRange.from, 'yyyy-MM-dd')
            const maxDateStr = format(this.dateRange.to, 'yyyy-MM-dd')

            // Check if unit is affordable at this AMI level
            if (
              isUnitAMIAffordable(unit.unit, prop.prop, minDateStr, maxDateStr, amiOption.value)
            ) {
              amiCounts[amiOption.value].count! += 1
            }
          }
        }
      }

      return Object.values(amiCounts)
    },
    getUnitTypeOptions(): Option[] {
      return getUnitTypes(this.allProperties).map((ut: UnitType) => ({
        label: this.unitTypeLabel(ut),
        value: ut,
        count: this.latestPricesByDateRangeOnly
          .flatMap((_) => _.units.map((_) => _.unit))
          .filter(
            (_: Availability) =>
              _.bed === ut.bed && _.bath === ut.bath && _.partial_bath === ut.partial_bath
          ).length,
        color: unitTypeColor(ut.bed)
      }))
    },
    getLeaseTermOptions() {
      const terms = new Set<number>()
      for (const prop of this.allProperties) {
        for (const unit of prop.building_availability) {
          for (const hist of unit.history) {
            for (const plan of hist.price_plans || []) {
              terms.add(plan[0])
            }
          }
        }
      }
      if (!terms.size) {
        return []
      }
      const sortedTerms = [...terms]
      sortedTerms.sort((a, b) => a - b)
      return [{ label: 'Not specified', value: null } as Option].concat(
        sortedTerms.map((duration_in_months) => ({
          label: duration_in_months === 0 ? 'Best' : `${duration_in_months} mo.`,
          value: duration_in_months
        }))
      )
    },
    computeExpandedUnitHistory() {
      const expandedUnitHistory: {
        [unit_id: string]: {
          created_on: string
          history: Price
        }[]
      } = {}
      for (const prop of this.allProperties) {
        for (const unit of prop.building_availability) {
          expandedUnitHistory[unit.id] = expandUnitHistory(unit, this.leaseTermSelectionSet)
        }
      }
      return expandedUnitHistory
    },
    openIntercom() {
      this.$tracker.openedIntercom()
      Intercom('show')
    },
    openReportDialog() {
      this.$tracker.openedGenerateReportDialog()
      this.showReportDialog = true
      this.fetchMapImage()
    },
    onMapSnapshot(url: string) {
      this.mapSnapshotUrl = url
    },
    onMapSnapshotAlternative(url: string) {
      this.mapSnapshotAlternativeUrl = url
    },
    fetchMapImage() {
      const mapComp = this.$refs.compGoogleMap as any
      if (mapComp?.createStaticMapUrl) {
        mapComp.createStaticMapUrl()
      }
      if (mapComp?.createStaticMapUrlAlternative) {
        mapComp.createStaticMapUrlAlternative()
      }
    },
    unitTypeLabel(unitType: { bed: number; bath: number; partial_bath: number | null }) {
      return `${unitType.bed}BR / ${unitType.bath}${unitType.partial_bath ? '.5' : ''}BA`
    },
    async forceLoadData(toLoad: {
      expenseBenchmarks: boolean
      oldRevenueManagement: boolean
      newRevenueManagement: boolean
    }) {
      const subject = this.subjectDetail
      if (subject === null) {
        return
      }

      if (
        toLoad.expenseBenchmarks &&
        this.availableExpenseBenchmarks &&
        subject.building_availability.some((_) => _.price !== null)
      ) {
        const promises: Promise<any>[] = []
        if (isNotStarted(usePropertyReportStore().subjectTaxHistory)) {
          promises.push(usePropertyReportStore().getTaxHistory(subject))
        }
        if (isNotStarted(usePropertyReportStore().expenseBenchmark)) {
          promises.push(usePropertyReportStore().getExpenseBenchmarks(subject, undefined))
        }
        await Promise.all(promises)
      }

      if (toLoad.newRevenueManagement && isNotStarted(usePropertyReportStore().revenueManagement)) {
        await usePropertyReportStore().getRevenueManagement(
          subject,
          [], // TODO: add comparables here. Disabled specifying comparables until we go full embedding
          {
            target_occupancy: 0.95,
            max_diff_percent: 0.1,
            timeframe_days: 30
          }
        )
      }

      if (toLoad.oldRevenueManagement && isNotStarted(usePropertyReportStore().unitPricings)) {
        await usePropertyReportStore().getOldRevenueManagement(subject, {
          max_radius: null,
          renovations: null,
          listing_status: ListingStatus.ALL,
          from_date: format(addDays(new Date(), -30), 'yyyy-MM-dd'),
          to_date: format(new Date(), 'yyyy-MM-dd'),
          include_outliers: false,
          building_ids: this.comparableAddresses ? this.comparableAddresses.map((_) => _.id) : null,
          excluded_unit_ids: [],
          exclude_subject: false,
          use_embedding: false
        } as PricingSettings)
      }
    },
    async getExcelJsonPayload() {
      await this.forceLoadData({
        expenseBenchmarks: true,
        oldRevenueManagement: false,
        newRevenueManagement: false
      })
      const expenseBenchmarkData = usePropertyReportStore().expenseBenchmark
      if (isLoaded(expenseBenchmarkData)) {
        const payload = excelPayload(
          this.similarities,
          this.subjectDetail,
          this.comparableDetails,
          expenseBenchmarkData.data || null
        ) as Record<string, unknown>
        if (payload === null) {
          // If payload is null, set a timeout and try again
          setTimeout(() => {
            this.getExcelJsonPayload().catch((e) => {
              console.error('An error occurred while downloading the JSON', e)
              this.$q.notify({
                message: 'An error occurred while downloading the JSON',
                color: 'negative',
                position: 'top'
              })
            })
          }, 100)
        }
        this.excelTestingPayload = payload
        this.showExcelTesting = true
      }
    },
    closeExcelTesting() {
      this.excelTestingPayload = null
      this.showExcelTesting = false
    },
    async downloadJson() {
      await this.forceLoadData({
        expenseBenchmarks: true,
        oldRevenueManagement: false,
        newRevenueManagement: false
      })
      const expenseBenchmarkData = usePropertyReportStore().expenseBenchmark
      if (isLoaded(expenseBenchmarkData)) {
        // downloads the Excel payload as a json file
        const payload = excelPayload(
          this.similarities,
          this.subjectDetail,
          this.comparableDetails,
          expenseBenchmarkData.data
        )
        if (payload === null) {
          setTimeout(() => {
            this.downloadJson().catch((e) => {
              console.error('An error occurred while downloading the JSON', e)
              this.$q.notify({
                message: 'An error occurred while downloading the JSON',
                color: 'negative',
                position: 'top'
              })
            })
          }, 100)
          return
        }
        const blob = new Blob([JSON.stringify(payload)], {
          type: 'application/json'
        })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        const formattedTime = format(new Date(), 'MM-dd-yyyy')
        const reportName = this.report ? this.report.name : 'Property-Report'
        link.download = reportName + '-' + formattedTime + '.json'
        link.click()
      }
    },
    async copyToClipboard(text: string) {
      await navigator.clipboard.writeText(text)
    },
    randomTip() {
      return tips[Math.floor(Math.random() * tips.length)]
    },
    async downloadExcel(useDevFile = false) {
      this.downloadExcelLoading = true
      await this.forceLoadData({
        expenseBenchmarks: true,
        oldRevenueManagement: false,
        newRevenueManagement: false
      })

      const expenseBenchmarkData = usePropertyReportStore().expenseBenchmark

      const payload = excelPayload(
        this.similarities,
        this.subjectDetail,
        this.comparableDetails,
        isLoaded(expenseBenchmarkData) ? expenseBenchmarkData.data : null
      )
      if (payload === null) {
        setTimeout(() => {
          this.downloadExcel(useDevFile).catch((e) => {
            console.error('An error occurred while downloading the Excel', e)
            this.$q.notify({
              message: 'An error occurred while downloading the Excel',
              color: 'negative',
              position: 'top'
            })
          })
        }, 100)
        return
      }
      const deletedTabs = undefined
      const hiddenTabs = !this.hasFinancialAnalysis
        ? 'Financial Analysis,Pro Forma Model'
        : undefined
      await templater
        .fillExcel(payload, useDevFile, hiddenTabs, deletedTabs)
        .then((response) => {
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          const formattedTime = format(new Date(), 'MM-dd-yyyy')
          const reportName = this.report ? this.report.name : 'Property-Report'
          link.download = reportName + '-' + formattedTime + '.xlsx'
          link.click()
        })
        .finally(() => {
          this.$tracker.exported('excel')
          this.downloadExcelLoading = false
        })
    },
    async downloadLegacyPdf() {
      let hadDarkModeOn = false
      if (this.$q.dark.isActive) {
        this.$q.dark.toggle()
        hadDarkModeOn = true
      }
      usePropertyReportStore().setDownloadPdfLoading(true)
      this.$emit('togglePrintMode', true)

      // load expense benchmarks and revenue management if they have been ticked, respectively
      await this.forceLoadData({
        expenseBenchmarks: this.ticked.includes(Section.FINANCIAL_ANALYSIS),
        oldRevenueManagement: this.ticked.includes(Section.PRICE_RECOMMENDATION),
        newRevenueManagement: this.ticked.includes(Section.RECOMMENDATION_HIGHLIGHTS)
      })

      const element = document.getElementById('pdf-area')
      const usPaperRatio = 8.5 / 11
      const canvasWidth = 2000
      const canvasHeight = canvasWidth * usPaperRatio
      const opt = {
        margin: 50,
        filename: this.pageName + '.pdf',
        pagebreak: {
          mode: 'css',
          after: '.break-after',
          before: '.break-before',
          avoid: '.avoid-break'
        },
        enableLinks: true,
        html2canvas: {
          unit: 'px',
          format: [canvasWidth, canvasHeight],
          allowTaint: true,
          useCORS: true,
          logging: false,
          scale: this.pdfHighQuality ? 1.5 : 1,
          pageOrientation: 'landscape'
        },
        jsPDF: {
          unit: 'px',
          format: [canvasWidth, canvasHeight],
          orientation: 'landscape'
        }
      }

      const orgName = useUserStore().organization?.display_name

      // wait for 1 second to allow the page to render
      await new Promise((resolve) => setTimeout(resolve, 1000))

      await html2pdf().then((func) => {
        func
          .default()
          .set(opt)
          .from(element)
          .toPdf()
          .get('pdf')
          .then((pdf: PDF) => {
            // add "HelloData, Inc." text plus the date to the bottom of each page
            const totalPages = pdf.internal.getNumberOfPages()

            // add the hellodata logo to the bottom of each page
            const footerImg = new Image()
            footerImg.src = '/hd-logo.png'

            const center = pdf.internal.pageSize.getWidth() / 2

            // add time range to the bottom of each page, centered
            const todayStr = new Date().toLocaleDateString()
            const rangeText = this.dateRange
              ? format(this.dateRange.from, 'MMM d, yyyy') +
                ' to ' +
                format(this.dateRange.to, 'MMM d, yyyy')
              : 'As of ' + todayStr
            const rangeTextWidth = pdf.getStringUnitWidth(rangeText)

            const linkUrl =
              'https://www.hellodata.ai?utm_source=pdf&utm_source=hellodata&utm_medium=pdf-footer&utm_campaign=' +
              orgName

            const pageHeight = pdf.internal.pageSize.getHeight()
            const pageWidth = pdf.internal.pageSize.getWidth()

            pdf.setFontSize(14)
            pdf.setTextColor(100)

            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i)

              pdf.text(rangeText, center - rangeTextWidth * 7, pageHeight - 30)

              // add logo
              pdf.addImage(footerImg, 'PNG', 50, pageHeight - 50, 100, 37)
              // add link overlay
              pdf.link(50, pageHeight - 50, 100, 25, {
                url: linkUrl
              })

              pdf.text(
                `As of ${todayStr} - Page ${i} of ${totalPages}`,
                pageWidth - 195,
                pageHeight - 30
              )
            }
          })
          .save()
          .then(() => {
            localStorage.setItem(this.localStorageExportSettingsKey, JSON.stringify(this.ticked))
            this.$tracker.exported('legacy_pdf')
            this.showPdfDialog = false
            usePropertyReportStore().setDownloadPdfLoading(false)
          })
          .catch((error: Error) => {
            console.error(error)
            this.$q.notify({
              message: 'Error downloading PDF',
              color: 'red'
            })
          })
          .finally(() => {
            this.showPdfDialog = false
            this.$emit('togglePrintMode', false)
            usePropertyReportStore().setDownloadPdfLoading(false)
            if (hadDarkModeOn) {
              this.$q.dark.toggle()
            }
          })
      })
    },
    async downloadPdf(mode: 'async' | undefined = undefined) {
      if (!this.useNewPdfDownload) {
        return this.downloadLegacyPdf()
      }
      // close the dialog with the new PDF system
      this.showPdfDialog = false
      this.$q.notify({
        message: 'PDF Download started, you can continue to browse reports while waiting.',
        color: 'positive',
        position: 'bottom'
      })

      usePropertyReportStore().setDownloadPdfLoading(true)

      const orgName = useUserStore().organization?.display_name
      if (orgName === undefined) {
        console.error('Missing Organization name')
        return
      }

      localStorage.setItem(this.localStorageExportSettingsKey, JSON.stringify(this.ticked))

      if (this.report === null) {
        console.error('Invalid report ID')
        this.$q.notify({
          message: 'An error occurred while exporting the report.',
          color: 'negative',
          position: 'top'
        })
        return
      }

      const createReportData = {
        reportType: 'property_report',
        filters: {
          dateRange: this.dateRange,
          listingsSelection: this.listingsSelection,
          selectedUnitType: this.selectedUnitType ?? [],
          selectedBuildings: this.selectedBuildings,
          sqftRange: this.sqftRange,
          leaseTermSelection: this.leaseTermSelection,
          includedAmenities: this.includedAmenities,
          excludedAmenities: this.excludedAmenities,
          includedAmiLevels: this.includedAmiLevels,
          excludedAmiLevels: this.excludedAmiLevels
        },
        ticked: this.ticked,
        componentOptions: this.componentOptions,
        name: this.report ? this.report.name : 'Property Report',
        address: this.subject?.street_address ?? null,
        organizationName: orgName ?? '',
        organizationLogo: this.organization?.logo_url ?? null,
        createdOn: new Date(),
        reportId: this.report.id,
        streetviewImageUrl: this.getStreetViewUrlUnion
      }

      if (mode === 'async') {
        await report.asyncCreate(createReportData)
        this.showPdfDialog = false
        usePropertyReportStore().setDownloadPdfLoading(false)
        this.$tracker.backgroundTaskPdfDownload()

        // start polling to keep the user in the loop
        useBackgroundTasksStore()
          .poll()
          .catch(() => {
            console.error('An error occurred running the task polling')
          })
        return
      }

      await report
        .create(createReportData)
        .then((response) => {
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(response.data)
          const formattedTime = format(new Date(), 'MM-dd-yyyy')
          const reportName = this.report ? this.report.name : 'Property-Report'
          link.download = reportName + '-' + formattedTime + '.pdf'
          link.click()

          this.$tracker.exported('pdf', this.ticked)
        })
        .catch((e) => {
          console.error(e)
          this.$q.notify({
            message: 'An error occurred while exporting the report.',
            color: 'negative',
            position: 'top'
          })
        })
        .finally(() => {
          this.showPdfDialog = false
          usePropertyReportStore().setDownloadPdfLoading(false)
          this.useNewPdfDownload = true
        })
    },
    changedDateRange(range: { from: Date; to: Date }) {
      this.$tracker.changedFilter('date_range', 'toolbar', {
        from: format(range.from, 'yyyy-MM-dd'),
        to: format(range.to, 'yyyy-MM-dd')
      })
      this.dateRange = range
    },
    changedSqftRange(range: { min: number | null; max: number | null }) {
      this.sqftRange = {
        min: range.min,
        max: range.max
      }
      this.$tracker.changedFilter('sqft_range', 'toolbar', {
        min: range.min,
        max: range.max
      })
    },
    isActiveSection(tab: TabName, section: Section): boolean {
      if (this.printingMode) {
        return (
          !this.componentsToNotPrint.includes(section) &&
          this.ticked.includes(section) &&
          this.visibleSections !== null &&
          this.visibleSections.includes(section)
        )
      }
      if (this.visibleSections && !this.visibleSections.includes(section)) {
        return false
      }
      return this.activeTab === tab
    },
    async makeCopy(name: string, redirect = true) {
      if (this.subject) {
        const subject = {
          id: this.subject.id,
          lat: this.subject.lat,
          lon: this.subject.lon,
          building_name: this.subject.building_name,
          building_name_alias: this.subject.building_name_alias,
          street_address: this.subject.street_address,
          street_address_alias: this.subject.street_address_alias,
          city: this.subject.city,
          state: this.subject.state,
          zip_code: this.subject.zip_code,
          year_built: this.subject.year_built,
          number_units: this.subject.number_units
        }
        const comps = this.comparableDetails.map((_) => ({
          id: _.id,
          lat: _.lat,
          lon: _.lon,
          building_name: _.building_name,
          building_name_alias: _.building_name_alias,
          street_address: _.street_address,
          street_address_alias: _.street_address_alias,
          city: _.city,
          state: _.state,
          zip_code: _.zip_code,
          year_built: _.year_built,
          number_units: _.number_units
        }))
        await usePropertyReportStore()
          .create({
            name,
            excluded_property_ids: [],
            property_edits: [],
            is_organization_visible: false,
            organization_visibility: null,
            subject,
            comparable_addresses: comps,
            tags: ['Active'],
            public_id: null,
            view_preferences: []
          })
          .then((report) => {
            if (report) {
              this.$tracker.createdReport(report.id, 'copy', 'property')
              if (redirect) {
                // redirect to the new report
                this.$router.push({ name: 'Property Report', params: { reportId: report.id } })
              }
            } else {
              this.$router.push({ name: 'Dashboard' })
            }
          })
      }
    },
    setDefaultDateRange(preset: PresetRange) {
      localStorage.setItem('defaultDateRange', preset)
      this.defaultDateRange = preset
      this.$tracker.setDefaultDateRange('date_range', 'toolbar', preset)
    },
    toggleStudent(propId: string, direction: boolean) {
      if (this.pricePerBed[propId] === direction) {
        return
      }
      this.$tracker.changedFilter('properties', 'toolbar', `price_per_bed_${direction}`)
      const multiplyPrices = (obj: object, bed: number) => {
        const bedMultiplier = bed || 1
        const keys = [
          'min_price',
          'price',
          'max_price',
          'min_effective_price',
          'effective_price',
          'max_effective_price'
        ]
        for (const priceType of keys) {
          if (obj[priceType]) {
            if (direction) {
              obj[priceType] *= bedMultiplier
            } else {
              obj[priceType] /= bedMultiplier
            }
          }
        }
      }
      const prop = this.allProperties.find((_) => _.id === propId)
      if (prop) {
        for (const avail of prop.building_availability) {
          const bedMultiplier = avail.bed || 1
          multiplyPrices(avail, bedMultiplier)
          for (const hist of avail.history) {
            multiplyPrices(hist, bedMultiplier)
          }
        }
        this.pricePerBed[propId] = direction
      }
    },
    handleAmenityApply(event: { include: Option[]; exclude: Option[] }) {
      this.includedAmenities = [...event.include]
      this.excludedAmenities = [...event.exclude]
      this.$tracker.changedFilter('amenities', 'toolbar', {
        includedTags: this.includedAmenities,
        excludedTags: this.excludedAmenities
      })
    },
    handleAmiLevelApply(event: { include: Option[]; exclude: Option[] }) {
      this.includedAmiLevels = [...event.include]
      this.excludedAmiLevels = [...event.exclude]
      this.$tracker.changedFilter('ami_levels', 'toolbar', {
        includedLevels: this.includedAmiLevels,
        excludedLevels: this.excludedAmiLevels
      })
    },
    withBestLeaseTerm(row: FilteredUnit, leaseTerms: Set<number | null>): FilteredUnit {
      const selectedPlan = getBestLeaseTermPlan(row.lastHistory, leaseTerms)
      if (!selectedPlan) {
        return row
      }

      const [leaseTerm, price, effectivePrice] = selectedPlan

      return {
        ...row,
        unit: {
          ...row.unit,
          lease_term: leaseTerm,
          price,
          effective_price: effectivePrice
        },
        lastHistory: {
          ...row.lastHistory,
          lease_term: leaseTerm,
          price,
          effective_price: effectivePrice
        }
      }
    },
    fillPerBed() {
      for (const prop of this.allProperties) {
        this.toggleStudent(prop.id, true)
      }
    },
    clearPerBed() {
      for (const prop of this.allProperties) {
        if (this.pricePerBed[prop.id]) {
          this.toggleStudent(prop.id, false)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.property-compare {
  max-width: 1600px;
}
.tab {
  font-size: 12px;
}
.modal-title {
  font-size: 20px;
  font-weight: 400;
}

@page {
  margin-top: 25mm;
  margin-bottom: 10mm;
}

@media print {
  .break-after {
    page-break-after: always;
  }

  // we don't want the last element to have a break after because it'll add an unnecessary blank page
  .property-compare > :last-child {
    .break-after {
      page-break-after: avoid !important;
    }
  }

  .property-compare {
    max-width: 1700px;
  }
}
</style>
